/**
 * StrategyFormDialog Component
 *
 * Modal dialog for creating and editing strategies.
 * Implements FR-005, FR-010, FR-011, FR-018 requirements with best practices from ui-components.
 *
 * Features:
 * - Dynamic title in edit mode: 'Edit {strategy name}' (FR-011)
 * - Strategy Blocks section in edit mode (FR-018)
 * - Unsaved changes detection with UnsavedChangesDialog
 * - Real-time field validation
 * - Focus management (auto-focus first field, return focus on close)
 * - Performance optimization (useCallback, useMemo)
 * - Full ARIA accessibility
 * - Helper text for user guidance
 *
 * Related: Sprint 9 (Task T039), Sprint 3 (FR-011, FR-018)
 * Patterns: ui-components/stories/playbook/04-CommonPatterns.mdx (CRUD)
 *          ui-components/stories/playbook/best-practices/12-CommonMistakes.mdx
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Typography, Box, Stack, CircularProgress, Button, FormControlLabel, Switch } from '@mui/material';
import {
  BaseDialog,
  DialogHeader,
  DialogContent,
  DialogFooter,
  SAccordion,
  StyledTextField,
  StyledTextArea,
  StyledSelectDropdown,
  UnsavedChangesDialog,
  useChangeDetection,
  SDialog,
  type ChangedField,
} from '@modules/ui-components';
// CompositeDataPicker from data-pickers module (replaces legacy DataReferenceTreeWrapper)
import { CompositeDataPicker } from '@modules/data-pickers';
import type { InsightAnalysisHydration } from '@modules/data-pickers';
import { insightSelectionFromPaths } from '@modules/data-pickers/utils/pathsToFirestoreFormat';
import {
  defaultAvailableBlocks,
  isDefaultAvailableBlocks,
  type AvailableBlocksValue,
} from './AvailableBlocksSection';
import { useBlockSubtypes } from '../hooks/useBlockSubtypes';
import { useBlockLibraryExamples } from '../hooks/useBlockLibraryExamples';
import { useSystemBlockLibraryExamples } from '../hooks/useSystemBlockLibraryExamples';
import { BlockGenerationModeReadback } from './BlockGenerationModeSelector';
import {
  GenerationReadinessStrip,
  LAUNCH_ALREADY_CREATED_REASON,
  launchReadinessStatus,
  readinessBlockerRowId,
  readinessDisabledHelperText,
} from './GenerationReadinessStrip';
import type { BlockLibrarySelection } from '@modules/shared-types';
import {
  DEFAULT_STRATEGY_BLOCK_GENERATION_MODE,
  STRATEGY_BLOCK_GENERATION_MODE_LABELS,
  STRATEGY_BLOCK_TYPE_CATALOG,
  type StrategyBlockGenerationMode,
} from '@modules/shared-types';
// FP-10: ONE feasibility contract — the dialog, the server intake gate and the
// CLI all call this same pure function. This is a RUNTIME import of
// `@modules/shared-types` from the frontend bundle (the module's package.json
// `main` points at `src/index.ts`; Vite resolves it through the `@modules`
// alias exactly as tsc does).
import {
  assessStrategyGenerationFeasibility,
  // W13 IS2: the create wizard measures its own thesis selection with the SAME
  // sentences the server's refusal and the CLI print (AC-W13-04/25).
  GLOBAL_INSIGHT_SCOPE,
  insightSelectionBudget,
  insightSelectionBudgetMessage,
  scopeWithoutThesesMessage,
  type AgentInsightSelection,
  type FeasibilityInsightScope,
  type FeasibilityVariable,
} from '@modules/shared-types';
import { StrategyService } from '@modules/firebase-core/services/StrategyService';
import type { Strategy } from '@segmently/schema-models/strategy';
import { useCompositeDataOrchestrator } from '../hooks/useCompositeDataOrchestrator';
import { BlocksListView } from './BlocksListView';
import { BlockFormDialog } from './BlockFormDialog';
import { StrategyBlockService } from '@modules/firebase-core/services/StrategyBlockService';
import type { StrategyBlock } from '@segmently/schema-models/strategy';
import { VariableSetEditor } from '@modules/product/components/VariableSetEditor';
import { variableVersionService } from '@modules/firebase-core/services/VariableVersionService';
import {
  InsightVariableGenerationService,
  type InsightVariableGenerationData,
} from '@modules/firebase-core/services/InsightVariableGenerationService';
import { useStrategyThemeSettings } from '@modules/themes/hooks/useStrategyThemeSettings';
import { ThemeV2EditorDialog } from '@modules/themes/components/ThemeV2EditorDialog';
import type { ThemeV2 } from '@modules/themes/types/themesV2.types';
import { themeV2ToOnboardingSettings } from '@modules/themes/utils';
import PaletteIcon from '@mui/icons-material/Palette';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { StrategyExportService } from '../services/StrategyExportService';
import { useAvailableAIModels, useProjectDefaultAIModel } from '@modules/ai-settings';
import { GuideService } from '@modules/firebase-core/services/GuideService';
import { STRATEGY_LIST } from '@test-kit/selectors/strategy-list';
import type { StrategySubmitError } from './StrategySubmitErrorAlert';
import {
  StrategyCreateWizard,
  AnalysisStep,
  TasksStep,
  InsightsStep,
  InsightVariablesStep,
  ProjectVariablesStep,
  SocialProofStep,
  DetailsToneStep,
  BlockTypesStep,
  LibraryStep,
  AiSettingsStep,
  ReviewStep,
  QuickCreateScreen,
  WizardFooter,
  useWizardSourceRows,
  useStrategyCreateMode,
  replaceInsightTheses,
  replacePlane,
  buildWizardRailItems,
  buildQuickCreateRows,
  INSIGHT_DEFAULT_PROJECTION_CAPTION,
  estimateGeneration,
  resolveModelPricing,
  QUICK_CREATE_ROW_STEP,
  WIZARD_STEPS,
  wizardStepIndex,
  type AnalysisStepProps,
  type QuickCreateRowKey,
  type ReviewAlsoSentRow,
  type SelectionPlane,
  type WizardFooterProps,
  type WizardRailItem,
  type WizardStepKey,
} from './strategyWizard';
import { STRATEGY_TYPE_LABELS, STRATEGY_TYPES, strategyTypeLabel } from '../constants/strategyTypeLabels';

/**
 * W13 IS2 fix round 1 — the two sentences the thesis step can gate the launch
 * with. Exported shape: ONE wording per state, reused by the launch reason, the
 * review row and the quick-create row so the three can never disagree.
 */
const INSIGHT_THESES_LOADING_REASON = 'Reading the pinned analysis\u2019 theses…';
const INSIGHT_THESES_UNREADABLE_REASON = 'Insight theses could not be read — the default projection '
  + 'will be used. Reopen the dialog, or fix the analysis, then create.';
/** The short form the review and quick-create rows carry. */
const INSIGHT_THESES_UNREADABLE_CAPTION = 'Insight theses could not be read — '
  + 'the default projection will be used';

export interface StrategyFormDialogProps {
  /** Whether dialog is open */
  open: boolean;

  /** Dialog mode: 'create' or 'edit' */
  mode: 'create' | 'edit';

  /** Project ID for strategy */
  projectId: string;

  /** Strategy ID (required for edit mode) */
  strategyId?: string;

  /** Initial strategy data (for edit mode) */
  initialData?: Strategy;

  /** Strategy service instance */
  strategyService: StrategyService;

  /** Callback when dialog is closed */
  onClose: () => void;

  /** Callback when strategy is saved successfully */
  onSave: (strategy: Strategy) => void;

  /** Optional ref to return focus to trigger button */
  triggerRef?: React.RefObject<HTMLElement>;

  /**
   * R6: navigate to the insight-variable approval surface (analysis dialog,
   * Variables tab, generation preselected). When provided, blocking
   * "variables not accepted" states render a "Review variables" action.
   */
  onReviewVariables?: (args: { analysisId: string; generationId: string }) => void;

  /**
   * How many strategies the project already has. `0` turns on the create
   * wizard's first-run vocabulary note; an ABSENT prop keeps the note hidden
   * (a caller that does not know the count must not claim "your first").
   */
  existingStrategyCount?: number;
}

interface FormData {
  name: string;
  description: string;
  strategyType: Strategy['strategyType'];
  style: string;
  toneOfVoice: string;
  designDescription: string;
  selectedSourceDataPaths: string[]; // Paths from CompositeDataPicker (@audience[id].actors[id].field)
}

/**
 * What the unsaved-changes guard compares (Task 11, AC-036).
 *
 * The form fields PLUS the pinned insight analysis: since Task 7 the pin is
 * first-class dialog state instead of an `@insight[...]` entry inside
 * `selectedSourceDataPaths`, so a guard watching the paths alone would miss the
 * single most consequential create-mode decision.
 */
type TrackedFormState = FormData & {
  pinnedAnalysisId: string | null;
  /**
   * B9 fix (D-2): the building blocks picked from the library, as ONE
   * order-insensitive signature. Tracking the array itself would diff it
   * element by element and report a wall of `libraryBlockSelections[i]` rows
   * (the same reason the selection plane collapses into one row below).
   */
  libraryBlockSelectionsSignature: string;
  /**
   * B9 fix (D-2): the block-type whitelist, `null` until the operator touches
   * it. The catalog load re-applies the all-on default asynchronously AFTER
   * the dialog opens, and a raw value would report that machine default as the
   * operator's edit — the defect Task 11 already fought on the selection plane.
   */
  availableBlocksSignature: string | null;
};

/** Order-insensitive signature of the library picks (see `TrackedFormState`). */
const libraryBlockSelectionSignature = (selections: BlockLibrarySelection[]): string =>
  selections.map((selection) => `${selection.source}:${selection.exampleId}:${selection.mode}`)
    .sort()
    .join('|');

/** Number of entries a `libraryBlockSelectionSignature` carries — the guard's «N selected». */
const librarySignatureCount = (signature: string): number =>
  (signature ? signature.split('|').length : 0);

/** Order-insensitive signature of the block-type whitelist (see `TrackedFormState`). */
const availableBlocksSignature = (value: AvailableBlocksValue): string => [
  [...(value.blockTypes ?? [])].sort().join(','),
  [...(value.subtypeSlugs ?? [])].sort().join(','),
].join('|');

/** Order-insensitive comparison of two selection-path lists. */
const sameSelection = (a: string[], b: string[]): boolean => (
  a.length === b.length
  && JSON.stringify([...a].sort()) === JSON.stringify([...b].sort())
);

interface FormErrors {
  name?: string;
  description?: string;
  strategyType?: string;
}

const DEFAULT_STRATEGY_MAX_TOKENS = 100000;
const ACCEPTED_INSIGHT_VARIABLE_STATUSES = new Set(['completed', 'reviewed', 'applied']);

/** The four path planes the social-proof step writes (W12 Part B, Task B3). */
const SOCIAL_PROOF_PLANES: SelectionPlane[] = ['review', 'rating', 'studyCase', 'bigNumber'];

/** Order-sensitive path-list comparison — a no-op write must never re-render. */
const samePathList = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((path, index) => path === b[index]);

type InsightVariableGenerationOption = InsightVariableGenerationData & {
  acceptedCount: number;
  totalCount: number;
};

/** `createdAt` as epoch millis — comparable across the string/Timestamp-object shapes the field carries. */
function generationCreatedAtMillis(createdAt: InsightVariableGenerationOption['createdAt']): number {
  if (typeof createdAt === 'string') {
    const millis = new Date(createdAt).getTime();
    return Number.isNaN(millis) ? 0 : millis;
  }
  if (createdAt && typeof createdAt === 'object' && 'seconds' in createdAt) {
    return createdAt.seconds * 1000;
  }
  return 0;
}

/** «21 Aug 2026» — UTC-pinned so the date never shifts with the reader's timezone (mirrors `InsightAnalysisSelect`'s `formatCompletedDate`). */
function formatGenerationCreatedAt(createdAt: InsightVariableGenerationOption['createdAt']): string {
  const millis = generationCreatedAtMillis(createdAt);
  if (!millis) return '';
  return new Date(millis).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export const StrategyFormDialog: React.FC<StrategyFormDialogProps> = ({
  open,
  mode,
  projectId,
  strategyId,
  initialData,
  strategyService,
  onClose,
  onSave,
  triggerRef,
  onReviewVariables,
  existingStrategyCount,
}) => {
  // Form state. Strategy Type is pre-defaulted (UXR C8 / AC-031): the only
  // required create-mode decisions are the Name and ONE insight analysis.
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    strategyType: mode === 'create' ? STRATEGY_TYPES[0] : undefined,
    style: '',
    toneOfVoice: '',
    designDescription: '',
    selectedSourceDataPaths: [],
  });

  const [errors, setErrors] = useState<FormErrors>({});
  /**
   * W12 Part B (Task B6, owner ruling 2026-09-10): screens are generated WITH
   * the block plan by default; the switch that turns them off lives on the
   * wizard's review step. Create mode only — edit mode never reads this state
   * (its own «Generate blocks with AI» confirm owns `generateForSavedScreens`),
   * so its behaviour is unchanged.
   */
  const [generateScreens, setGenerateScreens] = useState(mode === 'create');
  const projectDefaultModel = useProjectDefaultAIModel(projectId);
  const [aiModel, setAiModel] = useState<string>(projectDefaultModel.value);
  const [aiMaxTokens, setAiMaxTokens] = useState<number>(DEFAULT_STRATEGY_MAX_TOKENS);
  // SG-A: the strategy-owned dispatch mode. Pre-set to the historic behaviour
  // so the create path still needs ONE decision (FP-1); the product default
  // stays a qualification decision, so nothing here calls it 'recommended'.
  const [blockGenerationMode, setBlockGenerationMode] =
    useState<StrategyBlockGenerationMode>(DEFAULT_STRATEGY_BLOCK_GENERATION_MODE);
  const [selectedInsightVariableGenerationId, setSelectedInsightVariableGenerationId] = useState('');
  const [insightVariableGenerations, setInsightVariableGenerations] = useState<InsightVariableGenerationOption[]>([]);
  const [insightVariableGenerationsLoading, setInsightVariableGenerationsLoading] = useState(false);
  const [insightVariableGenerationError, setInsightVariableGenerationError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  /**
   * F-LIVE2-40: the create submit's own failure, rendered by whichever launch
   * surface is on screen — the wizard's review step or the quick-create screen.
   * `refused: true` = the server validated the request away and the orphan
   * document was compensated, so the dialog STAYS OPEN with nothing created;
   * `false` = the document survives and is handed to `onSave` on close (FW9-R3).
   */
  const [submitError, setSubmitError] = useState<StrategySubmitError | null>(null);
  /**
   * F-LIVE2-40, ruling FW9-R3: the strategy a NON-refused failure left behind.
   * The dialog stays open on that failure so the sentence is actually seen, so
   * the caller's list refresh cannot happen on the submit tick any more — it
   * happens when the operator closes: `onSave(strategy)` then `onClose()`.
   *
   * STATE, not a ref (ruling FW9-R5): it also gates both launch actions, so a
   * render must follow the moment it is set.
   */
  const [pendingCreatedStrategy, setPendingCreatedStrategy] = useState<Strategy | null>(null);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);

  // UXR C7: available-blocks whitelist (create mode). Defaults to everything ON;
  // the request omits the field while the selection stays at the default (AC-047).
  const blockSubtypeCatalog = useBlockSubtypes(projectId);
  const [availableBlocksValue, setAvailableBlocksValue] = useState<AvailableBlocksValue>(
    () => defaultAvailableBlocks([]),
  );
  const [availableBlocksTouched, setAvailableBlocksTouched] = useState(false);
  useEffect(() => {
    if (!availableBlocksTouched) {
      setAvailableBlocksValue(defaultAvailableBlocks(blockSubtypeCatalog.subtypes));
    }
  }, [availableBlocksTouched, blockSubtypeCatalog.subtypes]);

  // ── UXR C4: Generate for the SAVED strategy (edit-dialog footer entry) ──
  const [generateForSavedOpen, setGenerateForSavedOpen] = useState(false);
  const [generateForSavedSubmitting, setGenerateForSavedSubmitting] = useState(false);
  const [generateForSavedScreens, setGenerateForSavedScreens] = useState(true);
  const [lastGenerationTask, setLastGenerationTask] = useState<{ status: string; createdAt: Date | null } | null>(null);
  const savedStrategyStatus = (initialData as Record<string, any> | undefined)?.status as string | undefined;

  // AC-015: last-generation status line — one-shot read of the newest
  // generate-strategy task for this strategy.
  useEffect(() => {
    if (!open || mode !== 'edit' || !strategyId) return;
    let cancelled = false;
    (async () => {
      try {
        const { collection, getDocs, getFirestore, limit, orderBy, query, where } = await import('firebase/firestore');
        const db = getFirestore();
        const tasksRef = collection(db, 'projects', projectId, 'backend_tasks');
        const snapshot = await getDocs(query(
          tasksRef,
          where('strategyId', '==', strategyId),
          where('taskType', '==', 'generate-strategy'),
          orderBy('createdAt', 'desc'),
          limit(1),
        ));
        if (cancelled) return;
        const doc = snapshot.docs[0];
        setLastGenerationTask(doc ? {
          status: String(doc.data().status ?? 'unknown'),
          createdAt: doc.data().createdAt?.toDate?.() ?? null,
        } : null);
      } catch (error) {
        console.warn('[StrategyFormDialog] Failed to load last generation task:', error);
        if (!cancelled) setLastGenerationTask(null);
      }
    })();
    return () => { cancelled = true; };
  }, [open, mode, projectId, strategyId, generateForSavedSubmitting]);

  // UXR C5: the radio-pinned analysis is FIRST-CLASS form state. The @insight
  // path is still merged into the shared selection (hydration/persistence),
  // but the launch gate must not depend on it: the composite's selectAll waves
  // merge paths from one-render-stale refs and can transiently drop the path.
  const [pinnedAnalysisId, setPinnedAnalysisId] = useState<string | null>(null);
  const [pinnedAnalysisTaskIds, setPinnedAnalysisTaskIds] = useState<string[] | undefined>(undefined);
  /**
   * W13 (IS1, I2): the GLOBAL package's theses of the pinned analysis, so the
   * dialog can measure the operator's thesis selection BEFORE submit. Empty
   * when the analysis doc does not inline them — the check then stays inert and
   * the server's 400 remains the backstop.
   */
  const [pinnedAnalysisSelectInsights, setPinnedAnalysisSelectInsights] =
    useState<Array<{ id: string; category: string }>>([]);
  /** Human label of the pinned analysis — the readiness strip's «Insight analysis» fact. */
  const [pinnedAnalysisName, setPinnedAnalysisName] = useState<string | null>(null);
  /**
   * «Source data (AI)» expansion. Controlled since Task 10 so the readiness
   * strip's «Adjust in Source data» can open it; the default is unchanged
   * (collapsed in create mode — UXR C8 «works with defaults»).
   */
  const [sourceDataExpanded, setSourceDataExpanded] = useState(mode !== 'create');

  // ── W12 Part B: the create-mode wizard ──
  /** The step the create wizard shows. Reset on every open (like the pin). */
  const [activeStep, setActiveStep] = useState<WizardStepKey>('analysis');
  /** «Got it» on the first-run note — per dialog session, never persisted. */
  const [firstRunDismissed, setFirstRunDismissed] = useState(false);
  /**
   * Task B7: guided ⇄ quick, remembered PER PROJECT. The wizard always opens
   * first on a project that never switched (the hook defaults to `guided`); the
   * header link is the only way in or out, and it persists immediately — the
   * current dialog session switches with it.
   */
  const [createMode, setCreateMode] = useStrategyCreateMode(projectId);
  /**
   * A wizard step opened from quick create through a row's «Change». The rail
   * stays available (the step is a real step), but the footer becomes
   * «Back to quick create» / «Done» — both return here, keeping the change.
   */
  const [quickEditingFromQuickCreate, setQuickEditingFromQuickCreate] = useState(false);
  /** One instance per dialog; the step ⓘ resolves guides through it. */
  const guideService = useMemo(() => new GuideService(), []);

  /**
   * Task B2 (D1): the create-mode name is SUGGESTED from the pinned analysis
   * and the strategy type until the operator types into the field — then it
   * never re-derives, for the rest of the dialog session. Reset on every open
   * (like the pin) so a reopened dialog starts untouched again.
   */
  const nameTouchedRef = useRef(false);
  useEffect(() => {
    if (open) nameTouchedRef.current = false;
  }, [open]);

  // Strategy variables — local state for immediate UI updates on edit/delete
  const [strategyVars, setStrategyVars] = useState<Record<string, any>>((initialData as any)?.variables ?? {});
  // Block Examples Library (Phase 3): operator-selected building blocks.
  const [libraryBlockSelections, setLibraryBlockSelections] = useState<BlockLibrarySelection[]>([]);
  const blockLibraryExamples = useBlockLibraryExamples(projectId);
  const systemBlockLibrary = useSystemBlockLibraryExamples();
  // AC-P3-006: selections are per-creation — a reopened dialog starts clean
  // (the component instance survives close, so state must reset explicitly).
  useEffect(() => {
    if (open) setLibraryBlockSelections([]);
  }, [open]);
  // The dialog instance survives close, so the create-mode defaults have to be
  // re-applied on every open (same reason as the library selections above):
  // the accordion collapse AND the analysis pin. A pin that outlives its dialog
  // makes a reopened «Create Strategy» start half-decided — and it hid the
  // «nothing pinned» state from every test that reopens rather than reloads.
  useEffect(() => {
    if (!open) return;
    setSourceDataExpanded(mode !== 'create');
    if (mode === 'create') {
      setActiveStep('analysis');
      setVisitedSteps(new Set(['analysis']));
      // F-LIVE2-40: a refusal belongs to the creation it refused — a reopened
      // dialog must never open on the previous attempt's error.
      setSubmitError(null);
      setPendingCreatedStrategy(null);
      // Task B3: the selection defaults are per-creation, exactly like the pin.
      taskDefaultsRef.current = null;
      insightThesisDefaultsRef.current = null;
      insightVariableDefaultsRef.current = null;
      socialProofDefaultsRef.current = false;
      excludedProjectVariableIdsRef.current = new Set();
      setFirstRunDismissed(false);
      // Task B7: quick create starts on its own screen, never inside the step
      // a PREVIOUS creation happened to leave open.
      setQuickEditingFromQuickCreate(false);
      setPinnedAnalysisId(null);
      setPinnedAnalysisTaskIds(undefined);
      setPinnedAnalysisSelectInsights([]);
      setPinnedAnalysisName(null);
      // B9 fix (D-1): the generation belongs to the pin, so it goes with it —
      // otherwise the insight-variable plane keeps serving the PREVIOUS
      // session's accepted variables under «nothing pinned» (the auto-pick
      // effect below never re-runs while no analysis is selected).
      setSelectedInsightVariableGenerationId('');
      // SG-A: the dispatch mode is per-creation too — a reopened dialog starts
      // on the historic behaviour rather than the previous creation's choice.
      setBlockGenerationMode(DEFAULT_STRATEGY_BLOCK_GENERATION_MODE);
      // Task B6: same rule for the screens switch — every creation starts at
      // the product default, not at the previous creation's choice.
      setGenerateScreens(true);
      // W13 IS2 (ruling R1): quotes are OFF at the start of every creation —
      // never inherited from the previous one.
      setIncludeInsightSources(false);
      // B9 fix (D-2): and for the block-type whitelist. Re-arming the TOUCHED
      // flag is enough — the catalog effect above re-applies «everything on»
      // for the current subtype catalog, and the unsaved-changes baseline (a
      // null signature while untouched) then matches an untouched open again.
      setAvailableBlocksTouched(false);
    }
  }, [open, mode]);
  // AC-016/017/018: task-scoped project variables follow the selected routes.
  // Default ON in create mode (the design brief's «defaults become correct»);
  // the switch keeps the rule visible and reversible. Reset on every open, for
  // the same reason as the library selections above.
  const [variablesFollowTasks, setVariablesFollowTasks] = useState(true);
  /**
   * W13 IS2 (AC-W13-06): the theses THIS creation is grounded in, over the
   * whole loaded universe. Displayed rows follow the selected routes, so a
   * thesis hidden by an unticked task keeps its state and comes back with it
   * (the same rule the project variables follow, AC-018).
   */
  const [selectedInsightThesisIds, setSelectedInsightThesisIds] = useState<string[]>([]);
  /** W13 IS2 (ruling R1, AC-W13-24): do the actors' own words travel? Default OFF. */
  const [includeInsightSources, setIncludeInsightSources] = useState(false);
  useEffect(() => {
    if (open) setVariablesFollowTasks(true);
  }, [open]);

  /**
   * ── Wizard selection defaults (W12 Part B, Task B3) ───────────────────────
   *
   * The create-mode `CompositeDataPicker` is gone, and with it its `selectAll`
   * waves — so the DIALOG now applies the same defaults, once per decision:
   * the routes of the pinned analysis, the accepted variables of the chosen
   * generation, every project variable, every social-proof entity. Each ref
   * holds the key its plane was last defaulted for; `null` re-arms it.
   */
  const taskDefaultsRef = useRef<string | null>(null);
  /**
   * W13 IS2: the thesis selection is defaulted to «all» once per PINNED
   * ANALYSIS — the two analyses' theses are disjoint id namespaces, so a
   * re-pin is a new question, never the old one re-asked.
   */
  const insightThesisDefaultsRef = useRef<string | null>(null);
  const insightVariableDefaultsRef = useRef<string | null>(null);
  const socialProofDefaultsRef = useRef(false);
  /**
   * AC-018: a project variable the operator unchecks stays unchecked when the
   * routes (or the follow switch) move it back into view. Exclusions are
   * per-creation, never persisted.
   */
  const excludedProjectVariableIdsRef = useRef<Set<string>>(new Set());
  /** Rail status: a step the operator has actually seen reads as done. */
  const [visitedSteps, setVisitedSteps] = useState<Set<WizardStepKey>>(new Set(['analysis']));

  /** Rail navigation also records what the operator has actually seen. */
  const handleWizardStepChange = useCallback((key: WizardStepKey) => {
    setActiveStep(key);
    setVisitedSteps((prev) => (prev.has(key) ? prev : new Set([...prev, key])));
  }, []);

  // ── Task B7: quick create ────────────────────────────────────────────────

  /** Quick create is on screen (create mode, quick preference, no step open). */
  const quickCreateActive = mode === 'create' && createMode === 'quick';
  /**
   * The quick-create SCREEN is the surface on display — the wizard shell is not
   * mounted at all. Anything that speaks to the operator (F-LIVE2-40's submit
   * error) must render there, and nothing may route them to a wizard step they
   * cannot see.
   */
  const quickCreateSurfaceActive = quickCreateActive && !quickEditingFromQuickCreate;

  /**
   * Task B9d fix 6: the analysis list's own hydration state, reported by
   * `InsightAnalysisSelect`. Quick create judges readiness NEXT TO its launch
   * primary, so until the list has arrived (and, when it carries analyses, until
   * the auto-pin has resolved) the chip must read «Checking…» instead of
   * counting blockers against data that is not there yet — designer invariant 5.
   * A project with no completed analysis at all is NOT hydrating: it gets the
   * real verdict, which names exactly that.
   */
  const [analysisHydration, setAnalysisHydration] = useState<InsightAnalysisHydration>({
    loading: true,
    empty: false,
  });
  const handleAnalysisHydrationChange = useCallback((next: InsightAnalysisHydration) => {
    // Identity-stable update: the child reports on every render of its effect,
    // and a fresh object each time would loop the dialog.
    setAnalysisHydration((prev) => (
      prev.loading === next.loading && prev.empty === next.empty ? prev : next
    ));
  }, []);

  /** The header link: flips the preference, persists it, and applies it now. */
  const handleSwitchCreateMode = useCallback(() => {
    setCreateMode(createMode === 'quick' ? 'guided' : 'quick');
    setQuickEditingFromQuickCreate(false);
  }, [createMode, setCreateMode]);

  /** «Change» on a quick row — opens the step that OWNS the value it shows. */
  const handleQuickCreateChange = useCallback((key: QuickCreateRowKey) => {
    setQuickEditingFromQuickCreate(true);
    handleWizardStepChange(QUICK_CREATE_ROW_STEP[key]);
  }, [handleWizardStepChange]);

  /** «Back to quick create» and «Done» are the same move: the change is already state. */
  const handleQuickCreateReturn = useCallback(() => {
    setQuickEditingFromQuickCreate(false);
  }, []);

  // SPEC-019 (AC-P3-039): materialized (include/adapt) selections whose
  // blockType the «Available blocks» whitelist excludes. Server re-enforces.
  const libraryAvailabilityConflicts = useMemo(() => {
    if (libraryBlockSelections.length === 0) return [];
    if (isDefaultAvailableBlocks(availableBlocksValue, blockSubtypeCatalog.subtypes)) return [];
    const allowedTypes = new Set(availableBlocksValue.blockTypes ?? []);
    const docsByKey = new Map([
      ...blockLibraryExamples.examples.map((doc) => [`project:${doc.id}`, doc] as const),
      ...systemBlockLibrary.examples.map((doc) => [`system:${doc.id}`, doc] as const),
    ]);
    return libraryBlockSelections
      .filter((selection) => selection.mode !== 'pattern')
      .flatMap((selection) => {
        const doc = docsByKey.get(`${selection.source}:${selection.exampleId}`);
        if (!doc || allowedTypes.has(doc.scopeMetadata.blockType)) return [];
        return [{
          exampleId: selection.exampleId,
          name: doc.name,
          blockType: doc.scopeMetadata.blockType,
        }];
      });
  }, [libraryBlockSelections, availableBlocksValue, blockSubtypeCatalog.subtypes,
    blockLibraryExamples.examples, systemBlockLibrary.examples]);
  /**
   * F-LIVE2-40: the server refuses `adapt` + screens OFF with a 400
   * (`assertLibrarySelectionsMatchScreenPlan`) — an adapted example only reaches
   * the run through the screen phase. The review step previews that refusal as a
   * WARNING; ruling FW9-R1 keeps the primary enabled, so the server stays the
   * single gate and the client never invents a second disabled state.
   */
  const adaptNeedsScreens = mode === 'create'
    && !generateScreens
    && libraryBlockSelections.some((selection) => selection.mode === 'adapt');
  useEffect(() => {
    if (initialData) {
      setStrategyVars((initialData as any)?.variables ?? {});
    }
  }, [initialData]);

  useEffect(() => {
    if (!open || mode !== 'create') return;
    setAiModel(projectDefaultModel.value);
  }, [open, mode, projectDefaultModel.value]);

  // BlockFormDialog state (Sprint 5)
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [blockMode, setBlockMode] = useState<'create' | 'edit'>('create');
  const [selectedBlock, setSelectedBlock] = useState<StrategyBlock | null>(null);
  const addBlockButtonRef = useRef<HTMLButtonElement>(null);

  // ✅ Track if form has been initialized to prevent infinite loop
  const isInitializedRef = useRef<boolean>(false);

  // ✅ Composite Data Orchestrator - handles ALL CompositeDataPicker logic
  // Replaces useDataReferenceOrchestrator with modern data-pickers module
  // Key advantage: NO double data loading - hydrated data available directly
  const orchestrator = useCompositeDataOrchestrator({
    projectId,
    strategyService,
    initialSelection: formData.selectedSourceDataPaths,
    selectAll: mode === 'create',
    onSelectionChange: (data) => {
      // Change tracking is applied centrally by the tracking effect below
      // (Task 11): only that effect knows whether this selection is the
      // machine's own preselection or the operator's edit, and it also carries
      // the pinned analysis, which this callback cannot see.
      setFormData(prev => ({ ...prev, selectedSourceDataPaths: data.selectedIds }));
    },
    open,
  });

  // ✅ Change detection for unsaved changes - destructure methods for stable references
  const {
    startTracking,
    updateCurrent,
    isDirty,
    getChanges,
    reset,
    acceptChanges,
    revert,
  } = useChangeDetection<TrackedFormState>({
    debug: false, // Set to true for debugging
  });

  /**
   * ── Unsaved-changes baseline vs. machine-made preselection (Task 11, AC-036) ──
   *
   * The intake preselects routes, variables and social proof AFTER the dialog
   * opens, in several async waves: the pickers hydrate, `selectAll` fires,
   * Task 8's follow-the-routes rule re-runs, a pinned analysis narrows
   * everything again. Baselining once at open therefore counted the machine's
   * own defaults as the operator's edits, and every abandoned dialog was met
   * with «Unsaved Changes Detected» (audit defect D6).
   *
   * The rule implemented here: the SELECTION plane of the baseline keeps
   * following the machine until the operator touches the selection themselves.
   * A pointer/key event inside the source-data picker freezes it — captured
   * BEFORE the click changes anything, so the very first toggle is already a
   * change. Pinning an analysis re-arms it: the routes and variables that
   * follow a pin are the machine's answer to that ONE decision, so the pin
   * stays listed while its consequences do not.
   *
   * Deliberately NOT «re-baseline once when `orchestrator.loading` goes false»:
   * the dialog instance survives close, so on every REOPEN the orchestrator
   * still holds its hydrated data and `loading` is already false at open —
   * that rule would baseline before the preselection had even started.
   */
  const trackedBaselineRef = useRef<TrackedFormState | null>(null);
  /**
   * The state the dialog OPENED with, before any preselection re-baselining.
   * «Discard changes» restores this and not the moving baseline: writing the
   * machine's preselection back into `formData` on close would leave the next
   * open reading a selection the fresh form never asked for.
   */
  const pristineBaselineRef = useRef<TrackedFormState | null>(null);
  const selectionFollowsPreselectionRef = useRef(true);

  /** Opens a tracking session: this state is «what the operator found». */
  const beginTracking = useCallback((baseline: TrackedFormState) => {
    trackedBaselineRef.current = baseline;
    pristineBaselineRef.current = baseline;
    selectionFollowsPreselectionRef.current = true;
    startTracking(baseline);
  }, [startTracking]);

  /** The operator touched the selection — from here on it is theirs, not the machine's. */
  const freezeSelectionBaseline = useCallback(() => {
    selectionFollowsPreselectionRef.current = false;
  }, []);

  useEffect(() => {
    if (!open) return;
    const baseline = trackedBaselineRef.current;
    if (!baseline) return;

    const current: TrackedFormState = {
      ...formData,
      pinnedAnalysisId,
      // B9 fix (D-2): the create-mode block choices join the guard. Edit mode
      // has no step for either, so both stay at the empty baseline there.
      libraryBlockSelectionsSignature: libraryBlockSelectionSignature(libraryBlockSelections),
      // `null` at the DEFAULT whitelist, not merely «untouched»: unchecking a
      // block type and checking it back is no change at all — the same reason
      // `handleSubmit` omits `availableBlocks` while it is the default.
      availableBlocksSignature:
        mode === 'create' && !isDefaultAvailableBlocks(availableBlocksValue, blockSubtypeCatalog.subtypes)
          ? availableBlocksSignature(availableBlocksValue)
          : null,
    };
    if (
      selectionFollowsPreselectionRef.current
      && !sameSelection(baseline.selectedSourceDataPaths, current.selectedSourceDataPaths)
    ) {
      const rebaselined: TrackedFormState = {
        ...baseline,
        selectedSourceDataPaths: [...current.selectedSourceDataPaths],
      };
      trackedBaselineRef.current = rebaselined;
      startTracking(rebaselined);
    }
    updateCurrent(current);
  }, [
    open, formData, pinnedAnalysisId, startTracking, updateCurrent,
    mode, libraryBlockSelections, availableBlocksValue, blockSubtypeCatalog.subtypes,
  ]);

  // ✅ Strategy Block Service instance for BlocksListView (Sprint 4, FR-029)
  const strategyBlockService = useMemo(() => {
    if (!strategyId) return null;
    return new StrategyBlockService(projectId, strategyId);
  }, [projectId, strategyId]);

  const insightVariableGenerationService = useMemo(
    () => new InsightVariableGenerationService(projectId),
    [projectId]
  );

  useEffect(() => {
    if (!open || mode !== 'create') {
      return;
    }

    let cancelled = false;
    setInsightVariableGenerationsLoading(true);
    setInsightVariableGenerationError(null);

    insightVariableGenerationService.list({ limit: 20 })
      .then(async (generations) => {
        const withCounts = await Promise.all(
          generations.map(async (generation): Promise<InsightVariableGenerationOption> => {
            try {
              const variables = await insightVariableGenerationService.listVariables(generation.id);
              return {
                ...generation,
                acceptedCount: variables.filter(variable => variable.accepted === true).length,
                totalCount: variables.length,
              };
            } catch (error) {
              console.warn('[StrategyFormDialog] Failed to load insight variable count:', error);
              return { ...generation, acceptedCount: 0, totalCount: 0 };
            }
          })
        );

        if (!cancelled) {
          setInsightVariableGenerations(withCounts);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setInsightVariableGenerationError((error as Error)?.message || 'Failed to load insight variable generations');
          setInsightVariableGenerations([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setInsightVariableGenerationsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, mode, insightVariableGenerationService]);

  // ✅ Individual field validators (real-time validation)
  const validateName = useCallback((value: string): string | null => {
    if (!value.trim()) {
      return 'Name is required';
    }
    if (value.length > 100) {
      return 'Name must be less than 100 characters';
    }
    return null;
  }, []);

  // UXR C8 (AC-030): the description is a defaulted L1 detail, not a required
  // create decision — only the length cap remains.
  const validateDescription = useCallback((value: string): string | null => {
    if (value.length > 500) {
      return 'Description must be less than 500 characters';
    }
    return null;
  }, []);

  const validateStrategyType = useCallback((value: Strategy['strategyType']): string | null => {
    if (!value) {
      return 'Strategy type is required';
    }
    return null;
  }, []);

  // ✅ Clear field error if field becomes valid (real-time validation)
  const clearFieldError = useCallback(
    (fieldName: keyof FormErrors, value: string | Strategy['strategyType']) => {
      let isValid = false;

      switch (fieldName) {
        case 'name':
          isValid = validateName(value as string) === null;
          break;
        case 'description':
          isValid = validateDescription(value as string) === null;
          break;
        case 'strategyType':
          isValid = validateStrategyType(value as Strategy['strategyType']) === null;
          break;
      }

      // Clear error if field becomes valid
      if (isValid) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[fieldName];
          return newErrors;
        });
      }
    },
    [validateName, validateDescription, validateStrategyType]
  );

  // Full form validation
  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    const nameError = validateName(formData.name);
    if (nameError) newErrors.name = nameError;

    const descriptionError = validateDescription(formData.description);
    if (descriptionError) newErrors.description = descriptionError;

    const strategyTypeError = validateStrategyType(formData.strategyType);
    if (strategyTypeError) newErrors.strategyType = strategyTypeError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, validateName, validateDescription, validateStrategyType]);

  // ✅ Project and webhook loading now handled by orchestrator
  // No need for separate useEffect hooks

  // ✅ Load initial data and start change tracking
  useEffect(() => {
    // ✅ Reset initialization flag when dialog closes
    if (!open) {
      isInitializedRef.current = false;
      // Task 11: the next open starts its own tracking session.
      trackedBaselineRef.current = null;
      pristineBaselineRef.current = null;
      selectionFollowsPreselectionRef.current = true;
      return;
    }

    // ✅ Only initialize once when dialog opens to prevent infinite loop
    if (isInitializedRef.current) {
      return;
    }

    if (mode === 'edit' && initialData) {
      // ✅ Restore user selection from sourceDataMapping (new format)
      // Extract paths from DataReference[] format
      const savedPaths = (initialData.sourceDataMapping || [])
        .map(ref => ref.sourcePath)
        .filter((path): path is string => Boolean(path));

      console.log('[StrategyFormDialog] Edit mode - restoring paths from sourceDataMapping:', {
        sourceDataMappingCount: initialData.sourceDataMapping?.length || 0,
        extractedPaths: savedPaths.length,
      });

      const editData: FormData = {
        name: initialData.name || '',
        description: initialData.description || '',
        strategyType: initialData.strategyType,
        style: initialData.style || '',
        toneOfVoice: initialData.toneOfVoice || '',
        designDescription: initialData.designDescription || '',
        selectedSourceDataPaths: savedPaths, // ✅ Restore from sourceDataMapping
      };
      setFormData(editData);
      // ✅ Start tracking with original data (the pin is create-mode state, so
      // it is `null` here and stays `null` for the whole edit session). Edit
      // mode has neither a library step nor a block-type step, so both B9
      // signatures stay at their empty baseline for the whole session.
      beginTracking({
        ...editData,
        pinnedAnalysisId: null,
        libraryBlockSelectionsSignature: '',
        availableBlocksSignature: null,
      });
      isInitializedRef.current = true;
    } else if (mode === 'create') {
      // ✅ Default values for create mode with project data
      const emptyData: FormData = {
        name: 'AI Strategy',
        description: '',
        strategyType: 'IOS Onboarding',
        style: '',
        toneOfVoice: orchestrator.services.aiOrchestration.project?.toneOfVoice || '',
        designDescription: orchestrator.services.aiOrchestration.project?.designDescription || '',
        selectedSourceDataPaths: [],
      };
      setFormData(emptyData);
      // ✅ Start tracking with default data. The create-mode open effect above
      // clears the pin, so the baseline pin is `null` — picking an analysis is
      // a listed change, the preselection it triggers is not. The same effect
      // clears the library picks and re-arms the block-type whitelist, so both
      // B9 signatures baseline EMPTY here rather than at this render's (still
      // stale) state — the reset lands one render later.
      beginTracking({
        ...emptyData,
        pinnedAnalysisId: null,
        libraryBlockSelectionsSignature: '',
        availableBlocksSignature: null,
      });
      isInitializedRef.current = true;
    }
  }, [
    open,
    mode,
    initialData,
    // ✅ Use primitive values only to avoid infinite loop from object references
    orchestrator.services.aiOrchestration.project?.toneOfVoice,
    orchestrator.services.aiOrchestration.project?.designDescription,
    beginTracking,
    // Note: isInitializedRef is a ref, so it doesn't trigger re-renders
  ]);

  // Note: autoFocus prop on first field handles initial focus

  // ✅ Return focus to trigger when dialog closes
  const returnFocus = useCallback(() => {
    setTimeout(() => {
      if (triggerRef?.current) {
        triggerRef.current.focus();
      }
    }, 100);
  }, [triggerRef]);

  // ✅ Handle field changes with change tracking and real-time validation
  const handleFieldChange = useCallback(
    (field: keyof FormData, value: string | Strategy['strategyType']) => {
      // ✅ Use functional update to avoid stale closure.
      // Change tracking is applied by the tracking effect above (Task 11).
      setFormData(prev => ({ ...prev, [field]: value }));

      // ✅ Real-time validation - clear error if field becomes valid
      clearFieldError(field as keyof FormErrors, value);
    },
    [clearFieldError]
  );

  // ✅ Source data selection change is now handled by orchestrator
  // No need for separate handler - integrated in orchestrator's onSelectionChange

  // ✅ Build strategy data object (shared between save and AI create)
  const buildStrategyData = useCallback(() => {
    const convertedSourceData = orchestrator.firestoreData;
    const sourceDataMapping = formData.selectedSourceDataPaths.map(path => ({
      sourcePath: path,
    }));
    return {
      name: formData.name,
      description: formData.description,
      strategyType: formData.strategyType,
      style: formData.style || undefined,
      toneOfVoice: formData.toneOfVoice || undefined,
      designDescription: formData.designDescription || undefined,
      sourceData: convertedSourceData,
      sourceDataMapping,
      // Create mode: clone from project. Edit mode: keep current strategy variables (user may have edited/deleted)
      variables: mode === 'edit'
        ? strategyVars
        : structuredClone((orchestrator.services.aiOrchestration.project as any)?.variableSet?.variables ?? {}),
      variableSetSourceId: projectId,
      variableSetVersion: (orchestrator.services.aiOrchestration.project as any)?.variableSet?.currentVersion,
      // SG-A: create only. Editing the mode after creation is out of scope
      // (REMEDIATION-PLAN § SG2), so an edit-mode save must never rewrite it.
      ...(mode === 'create' ? { blockGenerationMode } : {}),
    };
  }, [formData, orchestrator, projectId, mode, strategyVars, blockGenerationMode]);

  // ── Strategy theme editing (edit mode only) ──
  const { themeSettings: strategyThemeData, update: updateStrategyTheme } = useStrategyThemeSettings({
    projectId,
    strategyId: strategyId || '',
    skipInitialLoad: mode !== 'edit' || !strategyId,
  });
  const [themeEditorOpen, setThemeEditorOpen] = useState(false);

  const strategyThemeForEditor: ThemeV2 | null = strategyThemeData
    ? {
        id: strategyId || '',
        name: formData.name || 'Strategy Theme',
        colors: strategyThemeData.colors,
        fonts: strategyThemeData.fonts,
        optionsListSettings: strategyThemeData.optionsListSettings,
        layoutSettings: strategyThemeData.layoutSettings,
        background: strategyThemeData.background,
        headerBar: strategyThemeData.headerBar,
        actionBar: strategyThemeData.actionBar,
        generatedMetadata: strategyThemeData.generatedMetadata,
        flowDocument: strategyThemeData.flowDocument!,
        createdAt: '',
        updatedAt: '',
      }
    : null;

  const handleSaveStrategyTheme = useCallback(async (updatedTheme: ThemeV2) => {
    await updateStrategyTheme(themeV2ToOnboardingSettings(updatedTheme, {
      sourceThemeId: strategyThemeData?.sourceThemeId,
    }));
  }, [updateStrategyTheme, strategyThemeData?.sourceThemeId]);

  // Clone project's active theme to strategy subcollection before strategy use.
  const cloneProjectThemeToStrategy = useCallback(async (newStrategyId: string) => {
    try {
      const snapshot = await strategyService.ensureThemeSettingsV2FromActiveProjectTheme(newStrategyId);
      if (snapshot) {
        console.log(`[StrategyFormDialog] Cloned active project theme to strategy ${newStrategyId}`);
      } else {
        console.log('[StrategyFormDialog] No active project theme to clone');
      }
    } catch (err) {
      // Non-blocking — strategy still works without theme
      console.warn('[StrategyFormDialog] Failed to clone project theme:', err);
    }
  }, [strategyService]);

  // Export strategy JSON — uses the same hydration as createWithAI (guarantees identical data)
  const handleExportStrategyJson = useCallback(async () => {
    try {
      const strategyOrch = orchestrator.services.aiOrchestration.strategyOrchestrator;
      const sourceDataIds = orchestrator.aiSourceDataIds;

      // Build payload using the same hydrator as createWithAI
      const payload = await strategyOrch.buildStrategyPayload(sourceDataIds, strategyId);

      // Enrich with form data (may differ from saved strategy)
      const strategyData = buildStrategyData();
      (payload as any).name = strategyData.name;
      (payload as any).description = strategyData.description;
      (payload as any).strategyType = strategyData.strategyType;
      (payload as any).toneOfVoice = strategyData.toneOfVoice;
      (payload as any).designDescription = strategyData.designDescription;

      // Use picker-selected variables (not all from Firestore)
      const selectedVars = orchestrator.hydratedData?.variables;
      if (selectedVars && selectedVars.length > 0) {
        const varsRecord: Record<string, any> = {};
        for (const v of selectedVars) {
          varsRecord[v.id] = v;
        }
        (payload as any).strategyVariables = varsRecord;
      } else {
        // Fallback to current strategy variables
        (payload as any).strategyVariables = strategyVars;
      }

      const envelope = {
        __segmently_strategy_export__: true,
        version: '1.0.0',
        type: 'create-strategy',
        payload,
        metadata: {
          exportedAt: new Date().toISOString(),
          projectId,
          strategyId: strategyId || 'new',
          strategyName: strategyData.name,
        },
      };

      const name = strategyData.name?.replace(/\s+/g, '_') || 'strategy';
      StrategyExportService.download(envelope, `strategy_${name}_${Date.now()}.json`);
    } catch (err) {
      console.error('[StrategyFormDialog] Export failed:', err);
      alert(`Export failed: ${(err as Error).message}`);
    }
  }, [projectId, strategyId, orchestrator.services.aiOrchestration.strategyOrchestrator, orchestrator.aiSourceDataIds, buildStrategyData]);

  // ✅ Save only (no AI) — creates strategy with variables but no AI-generated blocks
  const handleSaveOnly = useCallback(async () => {
    /**
     * Ruling FW9-R5b: this dialog session's strategy ALREADY EXISTS (a create
     * whose generation failed keeps the document — FW9-R3), so saving again
     * would create a SECOND one. The footer button is disabled with the reason
     * and the readiness chip says the same, and since FW9-R5c no close path
     * reaches this function either — so this guard is defence in depth, kept
     * because the rule belongs to the FUNCTION, not only to the button. Create
     * mode only: `pendingCreatedStrategy` is never set in edit mode.
     */
    if (pendingCreatedStrategy) return;
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      const strategyData = buildStrategyData();
      let savedStrategy: Strategy;

      if (mode === 'create') {
        savedStrategy = await strategyService.create(strategyData as Partial<Strategy>);
        // Clone project theme to new strategy before returning the saved strategy.
        if (savedStrategy.id) {
          await cloneProjectThemeToStrategy(savedStrategy.id);
        }
      } else {
        savedStrategy = await strategyService.update(strategyId!, strategyData as Partial<Strategy>);
      }

      acceptChanges();
      onSave(savedStrategy);
      onClose();
      reset();
      returnFocus();
    } catch (error) {
      console.error('[StrategyFormDialog] Error saving strategy:', error);
      alert(`Failed to save strategy: ${(error as Error)?.message || 'Unknown error'}`);
    } finally {
      setIsSubmitting(false);
    }
  }, [validateForm, buildStrategyData, mode, strategyService, strategyId, acceptChanges, onSave, onClose, reset, returnFocus, cloneProjectThemeToStrategy, pendingCreatedStrategy]);

  // Phase F: insight-first — generation requires a pinned InsightsAnalysis.
  // Create mode pins via the L0 Select: FIRST-CLASS STATE, never a selection
  // path (the composite's selectAll waves used to drop `@insight[...]`).
  // Edit mode reads the saved lockfile pin (`inputManifest.insightsAnalysisId`)
  // and only falls back to the legacy `@insight[...]` path for strategies
  // generated before the manifest existed.
  const pathInsightAnalysisId = orchestrator.aiSourceDataIds?.insightIds?.[0] ?? '';
  const manifestInsightAnalysisId = (initialData as Strategy | undefined)?.inputManifest?.insightsAnalysisId ?? '';
  const selectedInsightsAnalysisId = mode === 'create'
    ? (pinnedAnalysisId ?? pathInsightAnalysisId ?? '')
    : (manifestInsightAnalysisId || pathInsightAnalysisId);
  /**
   * W13 (IS1, AC-W13-07): the picker's insight paths read as a thesis SELECTION.
   * `@insight[a].fears[i2]` narrows the grounding to that thesis;
   * `@insight[a].fears` means the whole category; a bare `@insight[a]` — which
   * is what a fully ticked analysis collapses to — means «all of them» and
   * yields `undefined`, so a strategy nobody narrowed sends no key at all and
   * the request stays byte-identical to today's (AC-W13-34).
   */
  const insightSelection = useMemo(
    // The LIVE picker selection — the same value `orchestrator.aiSourceDataIds`
    // is built from and the submit handler sends, so the readiness verdict and
    // the request can never disagree (FP-7 «what you see is what is sent»).
    () => insightSelectionFromPaths(
      orchestrator.currentSelection,
      selectedInsightsAnalysisId || undefined,
    ),
    [orchestrator.currentSelection, selectedInsightsAnalysisId],
  );
  /**
   * W13 (IS1, I2) — the theses the pinned analysis HOLDS, ticked or not.
   *
   * It must come from the picker's UNFILTERED universe (`hydratedData.insights
   * .available`), never from `hydratedData.insights.analyses`: that one is
   * `pathsToJson`'s output, i.e. only what is ticked, so measuring against it
   * answers «available === selected» by construction and the starvation check
   * could never fire. The pin Select's copy is the fallback for a picker that
   * has not hydrated yet; an empty result leaves the check inert, never wrong.
   */
  const pinnedAnalysisGlobalInsights = useMemo<Array<{ id: string; category: string }>>(() => {
    const available = (orchestrator.hydratedData as any)?.insights?.available
      ?.find((analysis: any) => analysis?.id === selectedInsightsAnalysisId)?.insights;
    return Array.isArray(available) && available.length ? available : pinnedAnalysisSelectInsights;
  }, [orchestrator.hydratedData, selectedInsightsAnalysisId, pinnedAnalysisSelectInsights]);

  /**
   * W13 (IS1, fix round 2) — the EDIT-mode generation payload.
   *
   * Ruling: edit mode always sends an EXPLICIT enumeration of every ticked
   * global thesis plus `scopes: ['global']`. Omitting the key would make the
   * server reuse the strategy's frozen selection, so an owner who re-ticks
   * everything would silently keep an older narrowing — «all ticked» must mean
   * «all». The quotes switch is deliberately NOT sent (edit mode has none), so
   * the frozen value survives.
   *
   * Guard (fix round 3): without a hydrated universe the enumeration cannot be
   * made — and an empty `insightIds` is a REFUSAL, while an OMITTED key makes
   * the server reuse the frozen narrowing. Neither is acceptable, so this value
   * stays `undefined` and the ACTION waits instead (`insightUniverseState` →
   * `generateForSavedDisabledReason`); nothing is guessed and nothing is sent.
   *
   * Fix wave 6 (FW6-R1): «no narrowing expressed» no longer means «the operator
   * never saw one». A strategy with a frozen global narrowing has it PRELOADED
   * into the picker on open (the effect below), so the enumeration built here
   * states the frozen set until the operator changes it — the whole universe is
   * ticked only when the strategy really carries no narrowing (D-LIVE-5).
   */
  const editModeInsightSelection = useMemo(() => {
    if (mode !== 'edit' || !selectedInsightsAnalysisId) return undefined;
    const universe = pinnedAnalysisGlobalInsights;
    // NO fallback (fix round 3): the collapsing form is `undefined` for
    // «everything ticked», and an omitted key makes the server reuse the frozen
    // narrowing — the silent reuse W13-R24 forbids. Without the universe the
    // enumeration cannot be made, so the ACTION is disabled instead
    // (`insightUniverseState` below); nothing is guessed and nothing is sent.
    if (universe.length === 0) return undefined;
    const ticked = new Set(insightSelection?.insightIds ?? []);
    if (!insightSelection) {
      // No narrowing expressed: the whole analysis is ticked.
      for (const insight of universe) ticked.add(insight.id);
    } else {
      for (const insight of universe) {
        if (insightSelection.categories?.includes(insight.category)) ticked.add(insight.id);
      }
    }
    return { insightIds: [...ticked], scopes: ['global'] };
  }, [mode, selectedInsightsAnalysisId, pinnedAnalysisGlobalInsights, insightSelection]);

  /**
   * W13 (IS1, fix round 3) — can edit mode enumerate the ticked theses yet?
   *
   * `ready`       — the universe is here; the enumeration is possible.
   * `loading`     — the picker's analysis fetch is still in flight.
   * `unavailable` — hydration settled and this analysis yielded no theses.
   *
   * Only edit mode with a pinned analysis is gated: create mode and an unpinned
   * edit session answer `ready`, so neither changes.
   */
  const insightUniverseState = useMemo<'ready' | 'loading' | 'unavailable'>(() => {
    if (mode !== 'edit' || !selectedInsightsAnalysisId) return 'ready';
    if (pinnedAnalysisGlobalInsights.length > 0) return 'ready';
    const published = Array.isArray((orchestrator.hydratedData as any)?.insights?.available);
    return !published && orchestrator.loading ? 'loading' : 'unavailable';
  }, [
    mode,
    selectedInsightsAnalysisId,
    pinnedAnalysisGlobalInsights,
    orchestrator.hydratedData,
    orchestrator.loading,
  ]);

  /**
   * Why «Generate blocks with AI…» is unavailable. Rendered next to the button
   * in the last-generation status line — a disabled action always names its
   * reason (designer invariant 14, owner rule: never silent).
   *
   * W13 (fix round 3): edit-mode generation must send an EXPLICIT enumeration of
   * the ticked theses (W13-R24), and that needs the pinned analysis' universe.
   * Until it is there the action WAITS rather than sending a request the server
   * would answer by silently reusing an older narrowing.
   */
  const generateForSavedDisabledReason = mode === 'edit'
    ? (savedStrategyStatus === 'baked' || savedStrategyStatus === 'published'
      ? 'This version is baked (read-only). Start New Version on the canvas first.'
      : insightUniverseState === 'loading'
        ? 'Loading the analysis\u2019 insights…'
        : insightUniverseState === 'unavailable'
          ? `Insights of "${pinnedAnalysisName || selectedInsightsAnalysisId}" could not be read, `
            + 'so the theses this run would use are unknown. Reopen the dialog, or fix the analysis, then generate.'
          : undefined)
    : undefined;

  const selectedVariableIds = orchestrator.firestoreData.variableIds ?? [];
  const projectVariables = useMemo(
    () => (orchestrator.services.aiOrchestration.project as any)?.variableSet?.variables ?? {},
    [orchestrator.services.aiOrchestration.project]
  );
  const projectVariableCount = Object.keys(projectVariables).length;

  /**
   * ── The create wizard's own selection rows (Task B3) ──────────────────────
   *
   * Create mode no longer mounts a `CompositeDataPicker`, so the four selection
   * steps load their rows here. Edit mode keeps the picker and never enables
   * this hook.
   */
  const wizardSourceRows = useWizardSourceRows({
    projectId,
    analysisTaskIds: mode === 'create' ? pinnedAnalysisTaskIds : undefined,
    // W13 IS2: the «Insights» step's rows. Create mode only — edit mode keeps
    // the picker, which hydrates the same analysis for its own tree.
    analysisId: mode === 'create' ? (pinnedAnalysisId ?? undefined) : undefined,
    generationId: mode === 'create' ? selectedInsightVariableGenerationId : '',
    projectVariables,
    enabled: mode === 'create' && open,
  });

  /**
   * ── The base every selection write builds on (fix round 1, finding 1) ─────
   *
   * React flushes ALL the default effects of a commit before applying the state
   * they schedule, so two writes in the same flush would both read the same
   * render snapshot of `savedSelections` and the second would silently drop the
   * first (last-write-wins). Reachable: the pin landing before the social-proof
   * load, with both promise resolutions in one React batch — the task plane
   * vanished and `no_task_routes` blocked create with nothing to explain it.
   *
   * The ref carries each write's result forward INSIDE the flush; a selection
   * that arrives from elsewhere (hydration, edit-mode picker, discard) re-seeds
   * it, detected by array identity — the orchestrator memoises `compositeProps`
   * on its selection, so a new identity IS a new selection.
   */
  const selectionPathsFromOrchestrator = orchestrator.compositeProps.savedSelections;
  const latestSelectionPathsRef = useRef<string[]>(selectionPathsFromOrchestrator ?? []);
  const observedSelectionPathsRef = useRef<string[] | undefined>(selectionPathsFromOrchestrator);
  if (observedSelectionPathsRef.current !== selectionPathsFromOrchestrator) {
    observedSelectionPathsRef.current = selectionPathsFromOrchestrator;
    latestSelectionPathsRef.current = selectionPathsFromOrchestrator ?? [];
  }

  /**
   * The ONE write path. `build` receives the latest list (not the render
   * snapshot); `replacePlane` is the ONE builder of whole-entity paths and the
   * other planes keep their order, so `buildStrategyData` / `handleSubmit` /
   * feasibility see the same shape they always did. `operator` freezes the
   * unsaved-changes baseline — the machine's own defaults must not be reported
   * as the operator's edits (Task 11).
   */
  const applySelectionPaths = useCallback((
    build: (current: string[]) => string[],
    operator: boolean,
  ) => {
    const current = latestSelectionPathsRef.current;
    const next = build(current);
    if (samePathList(next, current)) return;
    latestSelectionPathsRef.current = next;
    if (operator) freezeSelectionBaseline();
    orchestrator.compositeProps.onSelectionChange?.(next);
  }, [orchestrator.compositeProps, freezeSelectionBaseline]);

  const writeSelectionPlane = useCallback((
    plane: SelectionPlane,
    ids: string[],
    operator: boolean,
  ) => {
    applySelectionPaths((current) => replacePlane(current, plane, ids), operator);
  }, [applySelectionPaths]);

  /** Social proof is ONE table over four planes — folded in a single write. */
  const socialProofRows = wizardSourceRows.socialProof.rows;
  const writeSocialProofSelection = useCallback((ids: string[], operator: boolean) => {
    const selected = new Set(ids);
    applySelectionPaths((current) => SOCIAL_PROOF_PLANES.reduce((paths, plane) => replacePlane(
      paths,
      plane,
      socialProofRows.filter((row) => row.plane === plane && selected.has(row.id)).map((row) => row.id),
    ), current), operator);
  }, [applySelectionPaths, socialProofRows]);

  /**
   * ── Fix wave 6 (FW6-R1, D-LIVE-5 / F-LIVE-16): the frozen narrowing is
   *    PRELOADED into the edit picker ──────────────────────────────────────────
   *
   * W13-R24's principle stands — edit mode always sends an explicit enumeration
   * of what is ticked, never an omitted key the server would answer by reusing
   * an older narrowing. Its SEED was wrong: the edit picker opened with nothing
   * ticked («Insight Analysis (required) (0)»), so `editModeInsightSelection`
   * read «no narrowing expressed» and ticked the whole universe. On DEV strategy
   * `2hVZdzSqsUE3ggTOKI5m` that turned a 6-thesis strategy into a 107-thesis
   * request — 124 591 characters against a 120 000 packet limit, refused
   * pre-spend, i.e. the strategy could not be regenerated from the dialog at all.
   *
   * So the dialog now SHOWS what the last run was grounded on: once per open, as
   * soon as the universe has hydrated, the insight plane is replaced with one
   * path per frozen thesis. The operator sees them ticked, may change them, and
   * the enumeration sends exactly that — «what you see is what is sent», now
   * honest in both directions. The write is the machine's (`operator: false`), so
   * the unsaved-changes baseline follows it.
   *
   * Untouched: a strategy with no frozen narrowing (no `insightSelection`, or an
   * empty one) keeps today's «everything ticked» behaviour byte-for-byte, and so
   * does create mode. Frozen ids the analysis no longer holds are dropped (their
   * count is logged); if NONE of them survive, nothing is written — the widening
   * is then the only honest reading, and the strip announces it.
   *
   * Non-global scopes in the frozen selection are ignored here: per-task
   * narrowing is merged server-side (FR-3), and this picker hydrates the global
   * package only (W13-R22).
   */
  const frozenInsightPreloadedRef = useRef(false);
  useEffect(() => {
    if (!open) frozenInsightPreloadedRef.current = false;
  }, [open]);
  useEffect(() => {
    if (!open || mode !== 'edit' || frozenInsightPreloadedRef.current) return;
    if (!selectedInsightsAnalysisId) return;
    const frozenIds = ((initialData as Strategy | undefined)?.inputManifest as
      { insightSelection?: { insightIds?: string[] } } | undefined)?.insightSelection?.insightIds;
    if (!Array.isArray(frozenIds) || frozenIds.length === 0) return;
    // The gate the Generate action already waits on: without the universe the
    // categories are unknown, so the preload waits with it rather than guessing.
    const universe = pinnedAnalysisGlobalInsights;
    if (universe.length === 0) return;
    frozenInsightPreloadedRef.current = true;
    const categories = new Map(universe.map((insight) => [insight.id, insight.category]));
    const preloaded = frozenIds
      .filter((id) => categories.has(id))
      .map((id) => ({ id, category: String(categories.get(id)) }));
    const dropped = frozenIds.length - preloaded.length;
    if (dropped > 0) {
      console.debug('[StrategyFormDialog] frozen insight theses no longer in the analysis — dropped', {
        analysisId: selectedInsightsAnalysisId, frozen: frozenIds.length, preloaded: preloaded.length, dropped,
      });
    }
    if (preloaded.length === 0) return;
    applySelectionPaths(
      (current) => replaceInsightTheses(current, selectedInsightsAnalysisId, preloaded),
      false,
    );
  }, [
    open, mode, selectedInsightsAnalysisId, initialData,
    pinnedAnalysisGlobalInsights, applySelectionPaths,
  ]);

  /**
   * ── B9 fix (D-1): a create-mode open starts from an EMPTY selection ───────
   *
   * The dialog instance survives close, and so does the orchestrator's
   * selection: the create-open effect above reset the pin, the step and the
   * defaults refs, but `orchestrator.firestoreData.tasks` still held the
   * routes of the PREVIOUS pin. The review step then reported «Task routes 3»
   * while the Tasks step showed its «nothing pinned» empty state — three
   * routes the operator could neither see nor untick, and `no_task_routes`
   * could never appear (DEV e2e, spec 18 / AC-024).
   *
   * EVERYTHING goes, not only the seven typed planes (B9 fix round 1): the
   * untyped paths — `@audience[...].actors[...]` and the legacy `@insight[...]`
   * — have no create-mode writer either, and nothing else resets them. The
   * orchestrator's hydration locks itself on the FIRST open of the instance,
   * so an EDIT session's audiences would otherwise ride into the next CREATE
   * request through `...orchestrator.aiSourceDataIds` (and into the new
   * strategy's `sourceDataMapping`), while a surviving `@insight[...]` would
   * satisfy `analysis_not_pinned` with an Analysis Select that shows nothing.
   * Create mode mounts no picker and runs no `selectAll` wave, so it can never
   * PRODUCE such a path: there is nothing here to preserve.
   *
   * The wizard's own planes are re-filled right after, by the defaults effects
   * below (project variables and social proof immediately; routes and
   * insight-derived variables once an analysis is pinned). The write is the
   * machine's, not the operator's (`operator: false`), so the unsaved-changes
   * baseline keeps following it and an untouched open still closes without a
   * prompt.
   *
   * Edit mode returns early: it restores its selection from
   * `initialData.sourceDataMapping`.
   */
  useEffect(() => {
    if (!open || mode !== 'create') return;
    applySelectionPaths(() => [], false);
    // `applySelectionPaths` is deliberately NOT a dependency: it is rebuilt on
    // every selection write, and re-running this effect would clear the
    // selection the operator just made. Once per open, like the pin reset.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode]);
  /**
   * FP-7 «what you see is what is sent»: the project-variable ids the SUBMIT
   * handler will send, read off the orchestrator selection — never off picker
   * internals. An empty `variableIds` means «all project variables» downstream
   * (`useProjectVariables: true` with `variableIds: undefined`), so the empty
   * case resolves to the full set rather than to zero.
   */
  const effectiveProjectVariableIds = useMemo<string[]>(() => (
    selectedVariableIds.length > 0
      ? selectedVariableIds.filter(variableId => Boolean(projectVariables[variableId]))
      : Object.keys(projectVariables)
  ), [selectedVariableIds, projectVariables]);
  const selectedProjectVariableCount = effectiveProjectVariableIds.length;
  // AC-016: ONE denominator (all project variables) split into the two kinds the
  // operator reasons about — globals always travel, task-scoped ones follow routes.
  const selectedGlobalVariableCount = useMemo(() => effectiveProjectVariableIds.filter((variableId) => {
    const taskIds = (projectVariables[variableId] as { taskIds?: string[] } | undefined)?.taskIds;
    return !Array.isArray(taskIds) || taskIds.length === 0;
  }).length, [effectiveProjectVariableIds, projectVariables]);
  const selectedTaskScopedVariableCount = selectedProjectVariableCount - selectedGlobalVariableCount;
  /**
   * The operator narrowed nothing — the picker selection is empty, so the
   * request omits `variableIds` and the server resolves the WHOLE project
   * package. Saying «N selected» there would contradict the «Variables (0)»
   * header the picker shows: the wording has to name the fallback instead
   * (FP-7 honest states).
   */
  const projectVariablesFallbackApplies = selectedVariableIds.length === 0;
  const variablesSummaryCaption = projectVariablesFallbackApplies
    ? `No variables excluded · all ${selectedProjectVariableCount} will be sent (${selectedGlobalVariableCount} global + ${selectedTaskScopedVariableCount} task-scoped)`
    : `${selectedProjectVariableCount} selected · ${selectedGlobalVariableCount} global + ${selectedTaskScopedVariableCount} task-scoped`;
  const selectedInsightVariableGeneration = useMemo(
    () => insightVariableGenerations.find(generation => generation.id === selectedInsightVariableGenerationId),
    [insightVariableGenerations, selectedInsightVariableGenerationId]
  );
  /**
   * Task 9 (AC-019/AC-020): the insight-derived plane. `insightVariableIds`
   * are the ids a submit WOULD send — read off the orchestrator selection, the
   * same «what you see is what is sent» discipline as the project variables.
   */
  const selectedInsightVariableIds = orchestrator.firestoreData.insightVariableIds ?? [];
  const acceptedInsightVariableCount = selectedInsightVariableGeneration?.acceptedCount ?? 0;
  /** «generation #N» — newest first in the dropdown, so the index is 1-based from the END. */
  const selectedInsightVariableGenerationOrdinal = useMemo(() => {
    const index = insightVariableGenerations.findIndex(
      generation => generation.id === selectedInsightVariableGenerationId,
    );
    return index < 0 ? 0 : insightVariableGenerations.length - index;
  }, [insightVariableGenerations, selectedInsightVariableGenerationId]);
  /**
   * AC-021: the pinned analysis HAS insight-variable generations but none of
   * them accepted anything. That is a legitimate, non-blocking state and the
   * section has to name it — the dropdown disables such generations, so the
   * operator would otherwise face an unexplained «(0)».
   */
  const pinnedAnalysisGenerations = useMemo(
    () => (selectedInsightsAnalysisId
      ? insightVariableGenerations.filter(generation => generation.analysisId === selectedInsightsAnalysisId)
      : []),
    [insightVariableGenerations, selectedInsightsAnalysisId],
  );
  const pinnedAnalysisHasNoAcceptedVariables = pinnedAnalysisGenerations.length > 0
    && pinnedAnalysisGenerations.every(generation => generation.acceptedCount === 0);
  const insightVariablesSummaryCaption = selectedInsightVariableGeneration
    ? `${selectedInsightVariableIds.length} of ${acceptedInsightVariableCount} accepted selected`
      + ` · from analysis ${selectedInsightVariableGeneration.analysisName || selectedInsightVariableGeneration.analysisId}`
      + `, generation #${selectedInsightVariableGenerationOrdinal}`
    : pinnedAnalysisHasNoAcceptedVariables
      ? '0 of 0 accepted selected · this analysis has no accepted insight-derived variables'
      : 'No insight-derived generation chosen — product variables only';
  const selectedInsightVariableGenerationIsStale = Boolean(
    selectedInsightVariableGeneration
    && selectedInsightsAnalysisId
    && selectedInsightVariableGeneration.analysisId !== selectedInsightsAnalysisId
  );
  const selectedInsightVariableGenerationIsIncomplete = Boolean(
    selectedInsightVariableGeneration
    && !ACCEPTED_INSIGHT_VARIABLE_STATUSES.has(selectedInsightVariableGeneration.status)
  );
  const selectedInsightVariableGenerationIsEmpty = Boolean(
    selectedInsightVariableGeneration
    && selectedInsightVariableGeneration.acceptedCount === 0
  );
  /** Where «review them in Insights» goes: the chosen generation, else the pinned analysis's newest one. */
  const insightVariablesReviewTarget = useMemo(() => {
    const generation = selectedInsightVariableGeneration ?? pinnedAnalysisGenerations[0];
    return generation
      ? { analysisId: generation.analysisId, generationId: generation.id }
      : null;
  }, [selectedInsightVariableGeneration, pinnedAnalysisGenerations]);

  const variablePackageBlockingReason = mode === 'create'
    ? (
      selectedInsightVariableGenerationIsStale
        ? 'Selected insight-derived variables belong to a different insights analysis.'
        : selectedInsightVariableGenerationIsIncomplete
          ? 'Selected insight-derived variables are not completed yet.'
          : selectedInsightVariableGenerationIsEmpty
            ? 'Selected insight-derived variables have no accepted variables.'
            : projectVariableCount === 0 && !selectedInsightVariableGenerationId
              ? 'No product variables or accepted insight-derived variables are selected.'
              : null
    )
    : null;

  // UXR C4: launch generation for the persisted strategy (resupplies name/
  // strategyType/sourceData from the saved doc; current strategyId reused).
  const handleConfirmGenerateForSaved = useCallback(async () => {
    if (!strategyId || !initialData) return;
    // The footer entry is already disabled with this reason; this is the
    // last-resort guard for a programmatic call (same shape as the SPEC-019
    // library-conflict guard in `handleSubmit`). A run whose grounding cannot be
    // stated is never started.
    if (generateForSavedDisabledReason) return;
    setGenerateForSavedSubmitting(true);
    try {
      const result = await orchestrator.services.aiOrchestration.strategyOrchestrator.generateForExistingStrategy(
        { ...(initialData as unknown as Record<string, unknown>), id: strategyId, variables: strategyVars } as never,
        {
          generateScreens: generateForSavedScreens,
          // W13 (IS1): what the operator has ticked NOW wins over the selection
          // this strategy was last generated with (the server reuses the frozen
          // one only when a request sends none).
          ...(editModeInsightSelection ? { insightSelection: editModeInsightSelection } : {}),
        },
      );
      if (!result.success) {
        // AC-044: failed start is non-destructive — report and keep the dialog.
        alert(`Generation failed to start: ${result.error ?? 'Unknown error'}\n\nThe existing blocks and screens are unchanged.`);
        return;
      }
      setGenerateForSavedOpen(false);
    } finally {
      setGenerateForSavedSubmitting(false);
    }
  }, [strategyId, initialData, orchestrator.services.aiOrchestration.strategyOrchestrator, strategyVars, generateForSavedScreens, editModeInsightSelection, generateForSavedDisabledReason]);

  // The ONE insight analysis (design brief «strategy-intake-feasibility»): the
  // pin lives in dialog state and travels to the server as
  // `sourceData.insightIds` / `insightsAnalysisId` built FROM that state.
  // It is deliberately NOT pushed into the composite selection any more —
  // a path that the pickers own can be rewritten by their own select-all waves,
  // which is exactly how the dialog used to show one analysis and send another.
  const handleAnalysisSelect = useCallback((selection: {
    analysisId: string;
    analysisName: string;
    taskIds: string[];
    globalInsights?: Array<{ id: string; category: string }>;
  }) => {
    setPinnedAnalysisId(selection.analysisId);
    setPinnedAnalysisTaskIds(selection.taskIds);
    setPinnedAnalysisSelectInsights(selection.globalInsights ?? []);
    setPinnedAnalysisName(selection.analysisName);
    // Task 11 (AC-036): the routes, variables and social proof that follow a
    // pin are the machine's answer to THIS one decision — re-arm the selection
    // baseline so the guard lists the pin, not its consequences.
    selectionFollowsPreselectionRef.current = true;
    // Task B3: a pin is a NEW question about routes and insight variables, not
    // the old one re-asked — both planes re-apply their defaults, exactly like
    // the picker's `selectAll` re-armed on a new allowed-task set. The project
    // variables and the social proof are project-scoped and stay as they are.
    taskDefaultsRef.current = null;
    // W13 IS2: and the thesis selection — a new analysis, new theses.
    insightThesisDefaultsRef.current = null;
    insightVariableDefaultsRef.current = null;
    excludedProjectVariableIdsRef.current = new Set();
    // Fix round 1 (review finding 1): a manual generation pick is remembered
    // ONLY against the pin it was made under. EVERY pin action — including a
    // re-pin of an analysis the operator had already visited — clears it, so
    // the auto-pick effect always re-derives fresh for the newly pinned
    // analysis instead of replaying a stale id left over from a DIFFERENT
    // analysis that happened to share the ref's stored id.
    manualGenerationPickAnalysisRef.current = null;
  }, []);

  /**
   * Task B2 (D1): the create-mode Name field, and (owner ruling 2) the
   * generation Select — both are handed to `AnalysisStep` as plain callbacks;
   * the RULES live here, not in the step component.
   */
  const handleNameChange = useCallback((value: string) => {
    nameTouchedRef.current = true;
    handleFieldChange('name', value);
  }, [handleFieldChange]);

  /**
   * An operator's manual generation pick is remembered per PINNED analysis —
   * a new pin invalidates it implicitly (the stored id can no longer match
   * `selectedInsightsAnalysisId`), so the auto-pick effect below resumes.
   */
  const manualGenerationPickAnalysisRef = useRef<string | null>(null);
  const handleGenerationChange = useCallback((id: string) => {
    manualGenerationPickAnalysisRef.current = selectedInsightsAnalysisId || null;
    setSelectedInsightVariableGenerationId(id);
  }, [selectedInsightsAnalysisId]);

  /**
   * The generations of the PINNED analysis that are actually usable — accepted
   * status, at least one accepted variable — newest first. Both the auto-pick
   * effect and the «Variable set» options below read this ONE list (owner
   * ruling 2, D1): an ineligible generation is never something an operator
   * would deliberately choose over «product variables only».
   */
  const eligibleAnalysisGenerations = useMemo(() => (
    insightVariableGenerations
      .filter((generation) => (
        generation.analysisId === selectedInsightsAnalysisId
        && ACCEPTED_INSIGHT_VARIABLE_STATUSES.has(generation.status)
        && generation.acceptedCount > 0
      ))
      .sort((a, b) => generationCreatedAtMillis(b.createdAt) - generationCreatedAtMillis(a.createdAt))
  ), [insightVariableGenerations, selectedInsightsAnalysisId]);

  /**
   * Owner ruling 2 (2026-09-10): once the generations for the pinned analysis
   * are known, pre-select the newest eligible one — `''` when none exists.
   * Never overrides an operator's own pick for the SAME analysis (the ref
   * above).
   */
  useEffect(() => {
    if (mode !== 'create') return;
    if (!selectedInsightsAnalysisId) return;
    if (manualGenerationPickAnalysisRef.current === selectedInsightsAnalysisId) return;
    setSelectedInsightVariableGenerationId(eligibleAnalysisGenerations[0]?.id ?? '');
  }, [mode, selectedInsightsAnalysisId, eligibleAnalysisGenerations]);

  /** The «Variable set» Select's options — rendered only when there is more than one (`AnalysisStep` hides it otherwise). */
  const analysisStepGenerationOptions = useMemo(() => (
    eligibleAnalysisGenerations.map((generation) => ({
      value: generation.id,
      label: `${formatGenerationCreatedAt(generation.createdAt)} · ${generation.acceptedCount}/${generation.totalCount} accepted`,
    }))
  ), [eligibleAnalysisGenerations]);

  /**
   * Task B2 (D1): suggest «<analysis> · <type label>» while the operator has
   * not typed into the Name field. Recomputed on every analysis pin AND every
   * strategy-type change — `nameTouchedRef` (set by `handleNameChange`, reset
   * on dialog open) is the ONLY thing that stops it.
   */
  useEffect(() => {
    if (mode !== 'create' || nameTouchedRef.current || !pinnedAnalysisName) return;
    const suggested = `${pinnedAnalysisName} · ${strategyTypeLabel(formData.strategyType)}`.slice(0, 100);
    setFormData((prev) => (prev.name === suggested ? prev : { ...prev, name: suggested }));
  }, [mode, pinnedAnalysisName, formData.strategyType]);

  // Accepted insight-variable counts per analysis (option chips): several
  // generations may exist per analysis — show the strongest accepted count.
  const acceptedVariableCountsByAnalysis = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const generation of insightVariableGenerations) {
      if (!generation.analysisId) continue;
      if (!ACCEPTED_INSIGHT_VARIABLE_STATUSES.has(generation.status)) continue;
      counts[generation.analysisId] = Math.max(counts[generation.analysisId] ?? 0, generation.acceptedCount);
    }
    return counts;
  }, [insightVariableGenerations]);

  // Deduped task refs for pickers AND variable views (task NAMES, not raw ids — UXR C6).
  const formTaskList = useMemo(() => {
    // Task B3: create mode has no picker to hydrate `hydratedData`, so the
    // wizard's own task rows are the source of NAMES there (the fallback below
    // can only produce raw ids).
    if (mode === 'create' && wizardSourceRows.tasks.rows.length > 0) {
      return wizardSourceRows.tasks.rows.map((task) => ({ id: task.id, name: task.name }));
    }
    const raw = orchestrator.hydratedData?.tasks?.length > 0
      ? orchestrator.hydratedData.tasks
      : (orchestrator.firestoreData as any)?.tasks ?? [];
    const seen = new Set<string>();
    return raw
      .map((t: any) => ({ id: t.id || String(t), name: t.name || t.id || String(t) }))
      .filter((t: { id: string }) => { if (seen.has(t.id)) return false; seen.add(t.id); return true; });
  }, [mode, wizardSourceRows.tasks.rows, orchestrator.hydratedData, orchestrator.firestoreData]);

  /**
   * ── Generation readiness (Task 10, AC-005…AC-008/AC-013/AC-024) ───────────
   *
   * The feasibility input is built ONLY from the values the submit handler
   * serialises, so the strip can never flatter the request (FP-7, AC-031):
   * - `pickersLoading` ← `orchestrator.loading` (pickers hydrating → `checking`,
   *   and the primary stays enabled — designer invariant 5);
   * - `taskIds`        ← `orchestrator.firestoreData.tasks` (the submitted routes);
   * - `analysisId`     ← the pin the request carries (`selectedInsightsAnalysisId`);
   * - `projectVariables` ← the project variable objects behind
   *   `effectiveProjectVariableIds` (Task 8 semantics: an EMPTY picker selection
   *   means «send the whole package», so it resolves to the full map, not to zero);
   * - `insightVariableIds` ← `orchestrator.firestoreData.insightVariableIds`.
   */
  const feasibilityProjectVariables = useMemo<Record<string, FeasibilityVariable>>(() => {
    const selected: Record<string, FeasibilityVariable> = {};
    for (const variableId of effectiveProjectVariableIds) {
      const variable = projectVariables[variableId] as {
        name?: string;
        required?: boolean;
        priority?: string;
        taskIds?: string[];
      } | undefined;
      if (!variable) continue;
      selected[variableId] = {
        id: variableId,
        name: variable.name ?? variableId,
        required: variable.required === true,
        priority: variable.priority,
        taskIds: Array.isArray(variable.taskIds) ? variable.taskIds : undefined,
      };
    }
    return selected;
  }, [effectiveProjectVariableIds, projectVariables]);

  /**
   * How many task-scoped project variables come BACK when the routes are
   * selected again — the number the readiness info row promises.
   *
   * Deliberately NOT `facts.taskScopedVariables`, which counts the CURRENT
   * selection and is 0 in exactly the state the row explains. And deliberately
   * scoped to the routes the pinned analysis offers rather than to the whole
   * project map: only those actually return (the whole map also holds variables
   * of routes this analysis does not span), and promising more would be the
   * kind of small lie this whole strip exists to kill.
   */
  const returningTaskScopedVariableCount = useMemo<number>(() => {
    const scoped = (Object.entries(projectVariables) as Array<[string, { taskIds?: string[] } | undefined]>)
      .filter(([, variable]) => Array.isArray(variable?.taskIds) && variable!.taskIds!.length > 0);
    const availableRoutes = mode === 'create' ? pinnedAnalysisTaskIds : undefined;
    if (!availableRoutes || availableRoutes.length === 0) return scoped.length;
    const routes = new Set(availableRoutes);
    return scoped.filter(([, variable]) => variable!.taskIds!.some(taskId => routes.has(taskId))).length;
  }, [projectVariables, mode, pinnedAnalysisTaskIds]);

  const submittedTaskIds = useMemo<string[]>(
    () => (orchestrator.firestoreData?.tasks ?? []),
    [orchestrator.firestoreData],
  );

  // ── Wizard steps 2–5: defaults, visibility and the operator's edits (B3) ──

  /**
   * AC-016/017/018: while «follow tasks» is on, a task-scoped variable of an
   * UNSELECTED route is HIDDEN — it is not sent either, and a permanently
   * unticked row that the operator cannot influence would only read as a bug.
   * Turning the switch off brings every row back.
   */
  const visibleProjectVariableRows = useMemo(() => {
    if (mode !== 'create' || !variablesFollowTasks) return wizardSourceRows.projectVariables.rows;
    const selectedRoutes = new Set(submittedTaskIds);
    const scoped = wizardSourceRows.projectVariables.taskScopedIds;
    return wizardSourceRows.projectVariables.rows.filter((row) => {
      const rowTaskIds = scoped[row.id];
      return !rowTaskIds || rowTaskIds.some((taskId) => selectedRoutes.has(taskId));
    });
  }, [mode, variablesFollowTasks, wizardSourceRows.projectVariables, submittedTaskIds]);

  const hiddenProjectVariableCount =
    wizardSourceRows.projectVariables.rows.length - visibleProjectVariableRows.length;

  /** Every route of the pinned analysis, once per pin (today's `selectAll`). */
  useEffect(() => {
    if (mode !== 'create' || !open) return;
    /**
     * B9 fix (D-1): NO PIN ⇒ NO ROUTES. The open-time plane reset above cannot
     * hold on its own: the reset and this effect run in the SAME flush, where
     * `pinnedAnalysisId` still reads the previous session's pin for one render
     * (its `setState` lands in the next one), so this effect used to re-apply
     * the outgoing analysis's routes over the just-cleared plane. Making the
     * plane a FUNCTION of the pin — empty while nothing is pinned — converges
     * it on the very next render, long before any step can be read, and also
     * covers any other way the pin can go away.
     */
    if (!pinnedAnalysisId) {
      taskDefaultsRef.current = null;
      writeSelectionPlane('task', [], false);
      return;
    }
    if (wizardSourceRows.tasks.loading) return;
    if (taskDefaultsRef.current === pinnedAnalysisId) return;
    taskDefaultsRef.current = pinnedAnalysisId;
    writeSelectionPlane('task', wizardSourceRows.tasks.rows.map((row) => row.id), false);
  }, [mode, open, pinnedAnalysisId, wizardSourceRows.tasks, writeSelectionPlane]);

  /**
   * ── W13 IS2: the thesis selection ─────────────────────────────────────────
   *
   * Every thesis of the pinned analysis is checked when the step opens — the
   * shipped behaviour is «all theses ground the run», and the step only lets
   * the operator narrow it (AC-W13-06). Re-armed per pin, like the routes.
   */
  useEffect(() => {
    if (mode !== 'create' || !open) return;
    if (!pinnedAnalysisId) {
      insightThesisDefaultsRef.current = null;
      // A functional update: an unconditional `[]` would re-render forever.
      setSelectedInsightThesisIds((prev) => (prev.length === 0 ? prev : []));
      return;
    }
    if (wizardSourceRows.insight.loading) return;
    if (insightThesisDefaultsRef.current === pinnedAnalysisId) return;
    insightThesisDefaultsRef.current = pinnedAnalysisId;
    setSelectedInsightThesisIds(wizardSourceRows.insight.rows.map((row) => row.id));
  }, [mode, open, pinnedAnalysisId, wizardSourceRows.insight]);

  /**
   * The theses the step DISPLAYS: the global package plus the packages of the
   * SELECTED routes. A task nobody generates for is not part of the run, so its
   * theses are neither offered nor narrowed — which is exactly why unticking
   * that task on the Tasks step leaves the request ready (AC-W13-04, case 3).
   */
  const visibleInsightThesisRows = useMemo(() => {
    if (mode !== 'create') return [];
    const routes = new Set(submittedTaskIds);
    return wizardSourceRows.insight.rows.filter((row) => (
      row.scope === 'global' || row.taskIds.some((taskId) => routes.has(taskId))
    ));
  }, [mode, wizardSourceRows.insight.rows, submittedTaskIds]);

  /** What a submit would actually send, in row order (FP-7). */
  const checkedInsightThesisIds = useMemo(() => {
    const selected = new Set(selectedInsightThesisIds);
    return visibleInsightThesisRows.filter((row) => selected.has(row.id)).map((row) => row.id);
  }, [visibleInsightThesisRows, selectedInsightThesisIds]);

  /**
   * The scopes the step displayed, measured (ruling W13-R22). The global
   * package and each selected task's package are separate id namespaces, so the
   * counts — and the refusal — are per scope, never across them.
   */
  const displayedInsightScopes = useMemo<FeasibilityInsightScope[]>(() => {
    if (mode !== 'create' || visibleInsightThesisRows.length === 0) return [];
    const checked = new Set(checkedInsightThesisIds);
    const measure = (rows: typeof visibleInsightThesisRows) => ({
      availableCount: rows.length,
      selectedCount: rows.filter((row) => checked.has(row.id)).length,
    });
    const scopes: FeasibilityInsightScope[] = [];
    const globalRows = visibleInsightThesisRows.filter((row) => row.scope === 'global');
    if (globalRows.length > 0) scopes.push({ scope: 'global', ...measure(globalRows) });
    for (const taskId of submittedTaskIds) {
      const taskRows = visibleInsightThesisRows.filter((row) => row.taskIds.includes(taskId));
      // A task the analysis has no package for was never displayed: it keeps
      // its default projection and this selection says nothing about it.
      if (taskRows.length === 0) continue;
      scopes.push({
        scope: 'task',
        taskId,
        taskName: formTaskList.find((task: { id: string }) => task.id === taskId)?.name ?? taskId,
        ...measure(taskRows),
      });
    }
    return scopes;
  }, [mode, visibleInsightThesisRows, checkedInsightThesisIds, submittedTaskIds, formTaskList]);

  /**
   * Ruling W13-R27 — does this selection NARROW anything?
   *
   * «Every displayed thesis checked» is not a selection: the request omits
   * `insightIds`, the server keeps its own top-N shortlist, and nothing about
   * the prompt changes. Only a real narrowing produces ids, scopes, a budget
   * warning and a starvation refusal.
   */
  const insightSelectionNarrows = visibleInsightThesisRows.length > 0
    && checkedInsightThesisIds.length !== visibleInsightThesisRows.length;

  /**
   * W13 IS2 fix round 1 (I1 + I3): why the thesis step cannot answer yet.
   *
   * `loading`  — a pin is being read; the rows on screen still belong to the
   *              PREVIOUS analysis, so nothing may be sent or claimed.
   * `error`    — the universe could not be read at all. The run would silently
   *              fall back to the default projection, which is the owner's
   *              decision to make, not ours (owner rule: never silent).
   */
  const insightThesisGateReason = useMemo<string | null>(() => {
    if (mode !== 'create' || !pinnedAnalysisId) return null;
    if (wizardSourceRows.insight.loading) return INSIGHT_THESES_LOADING_REASON;
    if (wizardSourceRows.insight.error) return INSIGHT_THESES_UNREADABLE_REASON;
    return null;
  }, [mode, pinnedAnalysisId, wizardSourceRows.insight.loading, wizardSourceRows.insight.error]);

  const insightRowsFailed = mode === 'create'
    && Boolean(pinnedAnalysisId)
    && !wizardSourceRows.insight.loading
    && Boolean(wizardSourceRows.insight.error);

  /** Task id → name, for the refusal's «Select all theses of "<task>"» (MF-5). */
  const insightScopeNames = useMemo<Record<string, string>>(() => {
    const names: Record<string, string> = {};
    for (const scope of displayedInsightScopes) {
      if (scope.taskId && scope.taskName) names[scope.taskId] = scope.taskName;
    }
    return names;
  }, [displayedInsightScopes]);

  /** The step's own inline refusal — the feasibility contract's own words. */
  const starvedInsightScopes = useMemo(() => displayedInsightScopes
    .filter((scope) => scope.availableCount > 0 && scope.selectedCount === 0)
    .map((scope) => ({
      scopeId: scope.taskId ?? GLOBAL_INSIGHT_SCOPE,
      message: scopeWithoutThesesMessage(scope),
      // MF-5 (invariant 26): the refusal carries its own way out, named.
      fixLabel: scope.taskName
        ? `Select all theses of "${scope.taskName}"`
        : 'Select all theses',
    })), [displayedInsightScopes]);

  /**
   * MF-5 — re-check every thesis of one scope, in place. `scopeId` is a task id;
   * `'global'` (or nothing, which is the «no thesis at all is selected» blocker)
   * puts the global package — and, for the empty form, every displayed row —
   * back. The write is the OPERATOR's, exactly like a checkbox click.
   */
  const handleSelectScopeTheses = useCallback((scopeId?: string) => {
    const inScope = (row: typeof visibleInsightThesisRows[number], scope: string): boolean => (
      scope === GLOBAL_INSIGHT_SCOPE ? row.scope === 'global' : row.taskIds.includes(scope)
    );
    /**
     * Fix round 3 — an UNKEYED call (the global-scope blocker and the «nothing
     * at all is selected» one both carry no task id) repairs exactly the scopes
     * that are STARVED, never every row: a task the operator narrowed on
     * purpose must not be re-checked by someone else's fix.
     */
    const scopeIds = scopeId
      ? [scopeId]
      : starvedInsightScopes.map((scope) => scope.scopeId);
    const rowsOfScope = visibleInsightThesisRows.filter((row) => (
      scopeIds.length === 0 ? true : scopeIds.some((scope) => inScope(row, scope))
    ));
    if (rowsOfScope.length === 0) return;
    setSelectedInsightThesisIds((prev) => (
      [...new Set([...prev, ...rowsOfScope.map((row) => row.id)])]
    ));
  }, [visibleInsightThesisRows, starvedInsightScopes]);

  /** AC-W13-25: «above the N recommended», per scope. A warning, never a gate. */
  const insightBudgetWarnings = useMemo(() => displayedInsightScopes
    .filter((scope) => scope.selectedCount > insightSelectionBudget(scope.scope))
    .map((scope) => insightSelectionBudgetMessage(scope)), [displayedInsightScopes]);

  /**
   * The operator's edit. `ids` are the VISIBLE rows that stay checked; a row the
   * routes currently hide keeps whatever state it had, so re-ticking its task
   * brings it back exactly as it was (AC-018's rule, applied to theses).
   */
  const handleInsightThesisSelectionChange = useCallback((ids: string[]) => {
    const visible = new Set(visibleInsightThesisRows.map((row) => row.id));
    const kept = new Set(ids);
    setSelectedInsightThesisIds((prev) => {
      const next = prev.filter((id) => !visible.has(id) || kept.has(id));
      for (const id of ids) if (!next.includes(id)) next.push(id);
      return next;
    });
  }, [visibleInsightThesisRows]);

  /** Every accepted variable of the chosen generation, once per generation. */
  useEffect(() => {
    if (mode !== 'create' || !open) return;
    if (wizardSourceRows.insightVariables.loading) return;
    if (insightVariableDefaultsRef.current === selectedInsightVariableGenerationId) return;
    insightVariableDefaultsRef.current = selectedInsightVariableGenerationId;
    writeSelectionPlane(
      'insightVariable',
      wizardSourceRows.insightVariables.rows.map((row) => row.id),
      false,
    );
  }, [
    mode, open, selectedInsightVariableGenerationId,
    wizardSourceRows.insightVariables, writeSelectionPlane,
  ]);

  /** Every social-proof entity, once per dialog session. */
  useEffect(() => {
    if (mode !== 'create' || !open) return;
    if (wizardSourceRows.socialProof.loading || socialProofDefaultsRef.current) return;
    socialProofDefaultsRef.current = true;
    writeSocialProofSelection(socialProofRows.map((row) => row.id), false);
  }, [mode, open, wizardSourceRows.socialProof.loading, socialProofRows, writeSocialProofSelection]);

  /**
   * Every VISIBLE project variable minus the operator's own exclusions. This is
   * both the default («all») and the follow-the-routes rule: when a route is
   * unticked its variables leave the request, and when it comes back they
   * return — unless the operator had excluded them by hand (AC-018).
   */
  useEffect(() => {
    if (mode !== 'create' || !open) return;
    const excluded = excludedProjectVariableIdsRef.current;
    writeSelectionPlane(
      'variable',
      visibleProjectVariableRows.filter((row) => !excluded.has(row.id)).map((row) => row.id),
      false,
    );
  }, [mode, open, visibleProjectVariableRows, writeSelectionPlane]);

  const handleTaskSelectionChange = useCallback((ids: string[]) => {
    writeSelectionPlane('task', ids, true);
  }, [writeSelectionPlane]);

  const handleInsightVariableSelectionChange = useCallback((ids: string[]) => {
    writeSelectionPlane('insightVariable', ids, true);
  }, [writeSelectionPlane]);

  const handleProjectVariableSelectionChange = useCallback((ids: string[]) => {
    const selected = new Set(ids);
    const excluded = new Set(excludedProjectVariableIdsRef.current);
    for (const row of visibleProjectVariableRows) {
      if (selected.has(row.id)) excluded.delete(row.id);
      else excluded.add(row.id);
    }
    excludedProjectVariableIdsRef.current = excluded;
    writeSelectionPlane('variable', ids, true);
  }, [visibleProjectVariableRows, writeSelectionPlane]);

  const handleSocialProofSelectionChange = useCallback((ids: string[]) => {
    writeSocialProofSelection(ids, true);
  }, [writeSocialProofSelection]);

  /** Social-proof ids currently in the request, in row order. */
  const selectedSocialProofIds = useMemo(() => {
    const submitted = new Set([
      ...(orchestrator.firestoreData?.reviews ?? []),
      ...(orchestrator.firestoreData?.ratings ?? []),
      ...(orchestrator.firestoreData?.studyCases ?? []),
      ...(orchestrator.firestoreData?.bigNumbers ?? []),
    ]);
    return socialProofRows.filter((row) => submitted.has(row.id)).map((row) => row.id);
  }, [orchestrator.firestoreData, socialProofRows]);

  /**
   * Task B3: in create mode the selection rows are loaded by the wizard, not by
   * the composite picker — so «are the pickers still hydrating?» is answered by
   * the wizard's own loaders. Reading `orchestrator.loading` there would pin the
   * strip to «Checking…» forever: without a mounted picker its loading state is
   * never reported and the hook's `?? true` fallback stands.
   */
  const wizardRowsLoading = wizardSourceRows.tasks.loading
    // W13 IS2 fix round 1 (I1): the thesis plane too. While a NEW analysis
    // loads, the plane still answers with the PREVIOUS analysis' rows, so a
    // verdict computed here would measure a selection of analysis A against the
    // pin of analysis B — and read «Ready».
    || wizardSourceRows.insight.loading
    || wizardSourceRows.insightVariables.loading
    || wizardSourceRows.socialProof.loading;
  const selectionLoading = mode === 'create' ? wizardRowsLoading : orchestrator.loading;

  /**
   * W13 (IS1, I2): the thesis selection, measured against the pinned analysis'
   * GLOBAL package — the only namespace this picker can address (ruling
   * W13-R22). Without this the refusal the server produces would first be seen
   * ~40 s after submit, inside a failed task, which is exactly the silent
   * refusal the owner rule forbids (spec issue 6).
   *
   * Only the per-scope counts are claimed here. An id the analysis does not
   * hold is left to the server: the doc-level `aggregatedInsights` can be
   * partial, and a false «not part of this analysis» is worse than a late one.
   */
  const feasibilityInsightSelection = useMemo(() => {
    /**
     * W13 IS2 — CREATE mode judges the wizard's own step, not the picker: the
     * «Insights» step is the only producer of a create-mode thesis selection,
     * and it measures every scope it DISPLAYED (ruling W13-R22). Nothing is
     * claimed while the rows are still loading or the analysis exposes none —
     * an empty universe would read as «starved» and refuse a perfectly good run.
     */
    if (mode === 'create') {
      if (!pinnedAnalysisId || wizardSourceRows.insight.loading) return undefined;
      if (visibleInsightThesisRows.length === 0) return undefined;
      // Ruling W13-R27: an unnarrowed selection sends no ids, so there is
      // nothing to refuse and nothing to warn about — the server's own
      // projection is what runs, exactly as it did before W13.
      if (!insightSelectionNarrows) return undefined;
      return {
        selectedIds: checkedInsightThesisIds,
        ...(pinnedAnalysisName ? { analysisName: pinnedAnalysisName } : {}),
        scopes: displayedInsightScopes,
      };
    }
    if (!insightSelection) return undefined;
    const selectedIds = insightSelection.insightIds ?? [];
    const selectedCategories = insightSelection.categories ?? [];
    const available = pinnedAnalysisGlobalInsights;
    const selectedCount = available.filter((insight) =>
      selectedIds.includes(insight.id) || selectedCategories.includes(insight.category)).length;
    return {
      selectedIds,
      ...(selectedCategories.length ? { selectedCategories } : {}),
      ...(pinnedAnalysisName ? { analysisName: pinnedAnalysisName } : {}),
      scopes: [{
        scope: 'global' as const,
        selectedCount,
        availableCount: available.length,
      }],
    };
  }, [
    mode,
    pinnedAnalysisId,
    wizardSourceRows.insight.loading,
    visibleInsightThesisRows,
    checkedInsightThesisIds,
    displayedInsightScopes,
    insightSelectionNarrows,
    insightSelection,
    pinnedAnalysisGlobalInsights,
    pinnedAnalysisName,
  ]);

  const feasibility = useMemo(() => assessStrategyGenerationFeasibility({
    pickersLoading: selectionLoading,
    taskIds: submittedTaskIds,
    analysisId: selectedInsightsAnalysisId || null,
    projectVariables: feasibilityProjectVariables,
    insightVariableIds: selectedInsightVariableIds,
    ...(feasibilityInsightSelection ? { insightSelection: feasibilityInsightSelection } : {}),
  }), [
    selectionLoading,
    submittedTaskIds,
    selectedInsightsAnalysisId,
    feasibilityProjectVariables,
    selectedInsightVariableIds,
    feasibilityInsightSelection,
  ]);

  /**
   * The routes the pinned analysis makes AVAILABLE, by name. In edit mode there
   * is no pin control, so the submitted routes ARE the available ones.
   */
  const readinessRouteNames = useMemo<string[]>(() => {
    const routeIds = (mode === 'create' ? pinnedAnalysisTaskIds : undefined) ?? submittedTaskIds;
    return routeIds.map((routeTaskId: string) => (
      formTaskList.find((task: { id: string }) => task.id === routeTaskId)?.name ?? routeTaskId
    ));
  }, [mode, pinnedAnalysisTaskIds, submittedTaskIds, formTaskList]);

  /**
   * W13 IS2 (AC-W13-07 / AC-W13-03) — «N of M theses» on the readiness strip.
   *
   * EDIT mode only, and only for a strategy that actually carries a frozen
   * selection: a strategy nobody narrowed says nothing here, and its strip
   * reads exactly as it did before W13 (AC-W13-34). The count reported is the
   * LIVE picker selection, never the frozen number — the operator may already
   * have changed it (FP-7).
   */
  const readinessInsightTheses = useMemo<{ selected: number; available: number } | undefined>(() => {
    // Create mode states it on the review step's «Also sent» row instead — the
    // rows layout must not repeat a number the screen already carries.
    if (mode !== 'edit') return undefined;
    // The frozen selection is written by the SERVER (IS1, `StrategyInputManifestInsightSelection`); the schema-models `Strategy`
    // type does not carry it yet, so it is read through a narrow cast —
    // the same discipline the other off-schema manifest reads use here.
    const frozen = ((initialData as Strategy | undefined)?.inputManifest as
      { insightSelection?: { insightIds?: string[] } } | undefined)?.insightSelection;
    const available = pinnedAnalysisGlobalInsights.length;
    if (!frozen?.insightIds || available === 0) return undefined;
    // What is TICKED now, measured against the analysis' universe — never the
    // frozen count, which the operator may already have changed (FP-7).
    // No clamp (fix round 1, minor 3): `selected > available` means the picker's
    // universe is only partly hydrated, and `Math.min` would have hidden that
    // behind a tidy «4 of 4». The strip states the count and names the gap.
    const selected = editModeInsightSelection?.insightIds?.length ?? frozen.insightIds.length;
    return { selected, available };
  }, [mode, initialData, pinnedAnalysisGlobalInsights, editModeInsightSelection]);

  /** Social-proof entities in the submitted selection, per kind. */
  const readinessSocialProof = useMemo(() => ({
    reviews: orchestrator.firestoreData?.reviews?.length ?? 0,
    ratings: orchestrator.firestoreData?.ratings?.length ?? 0,
    studyCases: orchestrator.firestoreData?.studyCases?.length ?? 0,
    bigNumbers: orchestrator.firestoreData?.bigNumbers?.length ?? 0,
  }), [orchestrator.firestoreData]);

  /**
   * AC-008 inline fix: put EVERY available route back into the selection. The
   * non-task paths are preserved verbatim — the fix owns the task plane only.
   */
  const handleSelectAllRoutes = useCallback(() => {
    const routeIds = (mode === 'create' ? pinnedAnalysisTaskIds : undefined) ?? [];
    if (routeIds.length === 0) return;
    // Task 11: an operator-driven selection edit, exactly like a checkbox click.
    // Task B3: the task plane is rewritten through the ONE path builder.
    writeSelectionPlane('task', routeIds, true);
  }, [mode, pinnedAnalysisTaskIds, writeSelectionPlane]);

  /**
   * «Adjust in Source data» — in create mode the selection lives on the wizard's
   * Tasks step, so the readiness strip navigates there; edit mode still expands
   * the accordion and scrolls it into view.
   */
  const handleAdjustSourceData = useCallback(() => {
    if (mode === 'create') {
      handleWizardStepChange('tasks');
      return;
    }
    setSourceDataExpanded(true);
    // The accordion mounts its content on expand; one frame is enough for the
    // section to exist before we scroll to it.
    requestAnimationFrame(() => {
      document
        .querySelector('[data-testid="strategy-form-section-source-data"]')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [mode, handleWizardStepChange]);

  /**
   * The variable-package gate is a HYDRATION artifact while the pickers load
   * (`projectVariables` is empty until the project doc arrives), and a primary
   * must never be blocked on hydration (designer invariant 5). While the
   * verdict is `checking` the dialog claims nothing about the package.
   */
  const gatingVariablePackageReason = feasibility.state === 'checking'
    ? null
    : variablePackageBlockingReason;

  /**
   * The label of the pinned analysis. Create mode gets it from the Select's own
   * selection; edit mode has no Select, so it falls back to an insight-variable
   * generation that already carries the analysis name, and finally to the raw
   * id — never to «—», which would read as «nothing pinned» (FP-7).
   */
  const readinessAnalysisName = pinnedAnalysisName
    ?? insightVariableGenerations.find(
      generation => generation.analysisId === selectedInsightsAnalysisId,
    )?.analysisName
    ?? (selectedInsightsAnalysisId || undefined);

  /**
   * ── W12 Part B, Task B6: AI settings and review ───────────────────────────
   *
   * Prices of the SELECTED model, per 1M tokens, from the same catalog
   * `AvailableModelSelector` offers. A model the catalog does not carry yields
   * no price, and the cost estimate then reads «—» instead of borrowing another
   * model's number.
   */
  const availableModels = useAvailableAIModels(
    // Scoped to «the create wizard is on screen». `StrategyFormDialog` is
    // MOUNTED for the whole project page (ProjectViewPage renders it gated on
    // `strategyService`, not on `open`), and the hook fires an availability
    // request on mount — an unscoped call would cost every project page two
    // API calls nobody asked for. A falsy projectId makes the hook a no-op.
    open && mode === 'create' ? projectId : undefined,
  );
  const modelPricing = useMemo(
    () => resolveModelPricing(availableModels.textModels, aiModel),
    [availableModels.textModels, aiModel],
  );

  /**
   * Where the review step's «Edit» affordances go. The readiness rows and the
   * «Also sent» rows share one map: a key names the step that OWNS the value,
   * never a scroll target (there is nothing to scroll — each value has a step).
   */
  const REVIEW_EDIT_TARGETS: Record<string, WizardStepKey> = useMemo(() => ({
    analysis: 'analysis',
    routes: 'tasks',
    insights: 'insights',
    'insight-variables': 'insightVars',
    variables: 'projectVars',
    'social-proof': 'proof',
    details: 'details',
    blockTypes: 'blockTypes',
    library: 'library',
    generation: 'generation',
  }), []);

  const handleReviewEdit = useCallback((key: string) => {
    const step = REVIEW_EDIT_TARGETS[key];
    if (step) handleWizardStepChange(step);
  }, [REVIEW_EDIT_TARGETS, handleWizardStepChange]);

  /**
   * «Also sent»: everything the request carries that has no line of its own on
   * the review step. Each value is read from the SAME state the submit handler
   * serialises (FP-7) — never from a picker internal.
   */
  const reviewAlsoSentRows = useMemo<ReviewAlsoSentRow[]>(() => {
    const toneParts = [
      strategyTypeLabel(formData.strategyType) || '—',
      formData.toneOfVoice.trim() ? formData.toneOfVoice.trim() : 'default tone',
      formData.designDescription.trim() ? 'design notes' : 'no design notes',
    ];
    const subtypeCount = availableBlocksValue.subtypeSlugs.length;
    const libraryCount = libraryBlockSelections.length;
    return [
      {
        // W13 IS2 (AC-W13-24): what the request says about the pinned analysis
        // — the theses it is grounded in, and whether the quotes travel.
        key: 'insights',
        label: 'Insights',
        // Ruling W13-R27 / I3: the row states what the REQUEST carries — the
        // default projection while nothing is narrowed, the read failure when
        // the universe never arrived, the exact count otherwise.
        value: `${insightRowsFailed
          ? INSIGHT_THESES_UNREADABLE_CAPTION
          : insightSelectionNarrows
            ? `${checkedInsightThesisIds.length} of ${visibleInsightThesisRows.length} theses`
            : INSIGHT_DEFAULT_PROJECTION_CAPTION}`
          + ` · Actor quotes: ${includeInsightSources ? 'on (up to 3 per thesis)' : 'off'}`,
      },
      { key: 'details', label: 'Details & tone', value: toneParts.join(' · ') },
      {
        key: 'blockTypes',
        label: 'Block types',
        value: `${availableBlocksValue.blockTypes.length} of ${STRATEGY_BLOCK_TYPE_CATALOG.length} types`
          + ` · ${subtypeCount} subtype${subtypeCount === 1 ? '' : 's'}`,
      },
      {
        key: 'library',
        label: 'Building blocks',
        value: libraryCount === 0
          ? 'none'
          : `${libraryCount} library example${libraryCount === 1 ? '' : 's'}`,
      },
      {
        key: 'generation',
        label: 'AI settings',
        value: `${modelPricing.shortName || aiModel}`
          + ` · ${STRATEGY_BLOCK_GENERATION_MODE_LABELS[blockGenerationMode].label}`
          + ` · ${aiMaxTokens.toLocaleString('en-US').replace(/,/g, ' ')} tokens`,
      },
    ];
  }, [
    checkedInsightThesisIds,
    visibleInsightThesisRows,
    insightSelectionNarrows,
    insightRowsFailed,
    includeInsightSources,
    formData.strategyType,
    formData.toneOfVoice,
    formData.designDescription,
    availableBlocksValue,
    libraryBlockSelections,
    modelPricing.shortName,
    aiModel,
    blockGenerationMode,
    aiMaxTokens,
  ]);

  /**
   * What creating the strategy CLONES — a consequence, not an input (AC-022).
   *
   * Task B9d must-fix 4 (+ fix round 1): TWO numbers, each from the source that
   * actually produces it.
   * - `clonedCount` = the WHOLE project variable set — literally what the submit
   *   handler clones (`structuredClone(project.variableSet.variables)` above),
   *   so the sentence cannot promise a copy of a number that is not copied.
   * - `sentCount` = `feasibilityProjectVariables`, the package this generation
   *   sends — the same number the readiness «Project variables» row states.
   * `needReviewNames` follows the CLONED set: a variable without an analytics
   * decision is cloned (and will have to be resolved) whether or not this
   * particular generation uses it.
   */
  const reviewVariablesSummary = useMemo(() => ({
    clonedCount: Object.keys(projectVariables).length,
    sentCount: Object.keys(feasibilityProjectVariables).length,
    needReviewNames: (Object.values(projectVariables) as any[])
      .filter((variable) => variable?.excludeFromAnalytics === undefined)
      .map((variable) => variable?.name || variable?.id)
      .filter(Boolean) as string[],
    hasGeneration: Boolean(selectedInsightVariableGeneration),
  }), [feasibilityProjectVariables, projectVariables, selectedInsightVariableGeneration]);

  /** AC-007: the ONE helper sentence under the form when the primary is gated. */
  const readinessHelperText = readinessDisabledHelperText(feasibility);
  /**
   * SPEC-019 (m46): the whitelist×library contradiction as ONE footer line —
   * same words as the step-8 Alert, plus the step that can fix it. `handleSubmit`
   * refuses such a request, so it must gate the primary rather than let a click
   * do nothing (designer invariant 14, FP-6).
   */
  const libraryConflictReason = useMemo(() => {
    const [first] = libraryAvailabilityConflicts;
    if (!first) return null;
    const more = libraryAvailabilityConflicts.length - 1;
    return `«${first.name}» is a ${first.blockType} block, which «Block types» excludes`
      + `${more > 0 ? ` (and ${more} more example${more === 1 ? '' : 's'})` : ''}`
      + ` — remove the example or allow ${first.blockType} on «Building blocks».`;
  }, [libraryAvailabilityConflicts]);
  /** The blocker element the primary's `aria-describedby` points at. */
  const readinessDescribedBy = feasibility.blockers.length > 0
    ? readinessBlockerRowId(feasibility.blockers[0])
    : undefined;

  /**
   * W13 IS2 — what a create-mode submit says about the pinned analysis.
   *
   * The key travels ONLY when there is something to say (ruling W13-R27): a real
   * narrowing, or the quotes switch turned on. Every displayed row checked IS
   * the server's own default projection, so an unnarrowed, quotes-off run sends
   * no key at all and freezes no hollow selection into the input manifest
   * (AC-W13-31, AC-W13-34). When it does travel, `scopes` names every scope the
   * step displayed, so a task whose theses were all unchecked is refused by name
   * while a scope the step never showed keeps its projection (W13-R22).
   */
  const createInsightSelection = useMemo<AgentInsightSelection | undefined>(() => {
    if (mode !== 'create' || !selectedInsightsAnalysisId) return undefined;
    // I1: while the plane loads, the rows on screen belong to the PREVIOUS
    // analysis — sending their ids under the new pin is the one thing this
    // value must never do. The launch is gated with the same reason.
    if (wizardSourceRows.insight.loading) return undefined;
    if (!insightSelectionNarrows) {
      // Ruling W13-R27: nothing is narrowed, so the ONLY thing left to say is
      // the quotes switch — and an all-default run says nothing at all, so no
      // hollow object is frozen into the strategy's input manifest.
      return includeInsightSources ? { includeSources: true } : undefined;
    }
    return {
      insightIds: checkedInsightThesisIds,
      scopes: displayedInsightScopes.map((scope) => scope.taskId ?? GLOBAL_INSIGHT_SCOPE),
      includeSources: includeInsightSources,
    };
  }, [
    mode,
    selectedInsightsAnalysisId,
    wizardSourceRows.insight.loading,
    insightSelectionNarrows,
    checkedInsightThesisIds,
    displayedInsightScopes,
    includeInsightSources,
  ]);

  // ✅ Handle save with AI (original flow)
  const handleSubmit = useCallback(async () => {
    if (!validateForm()) {
      return;
    }
    // SPEC-019 (m46): the primary is already disabled with this reason
    // (`libraryConflictReason` → `wizardLaunchDisabledReason`) and the rail
    // paints «Building blocks» red; this is the last-resort guard for the edit
    // form and for a programmatic call. The server re-enforces it either way.
    if (libraryAvailabilityConflicts.length > 0) {
      return;
    }
    // W13 IS2 fix round 1: the same last-resort shape for the thesis selection.
    // The primary is already disabled with the reason (the step's inline
    // refusal, the readiness blocker or the loading/unreadable gate), so this
    // returns silently — it exists for a programmatic call, and the server
    // re-enforces every one of these cases.
    if (mode === 'create' && (starvedInsightScopes.length > 0 || insightThesisGateReason)) {
      return;
    }
    /**
     * Ruling FW9-R5b — the last-resort shape of the already-created gate, and
     * since FW9-R5c unreachable by the UI (both launch buttons are disabled with
     * the reason, the chip reports it, and no close path submits any more). It
     * stays on purpose: the rule belongs to the function, so a second
     * `createWithAIServer` for this dialog session — from any future caller —
     * cannot create a SECOND strategy document for the same attempt. No new
     * copy: the surfaces already say why.
     */
    if (pendingCreatedStrategy) {
      return;
    }

    setIsSubmitting(true);
    console.log('[StrategyFormDialog] handleSubmit called, mode:', mode);

    try {
      let savedStrategy: Strategy;

      if (mode === 'create') {
        console.log('[StrategyFormDialog] Creating strategy with AI (server pipeline)...');

        // What the Select shows is what is sent (FP-7 «honest states»): the
        // insight source is rebuilt FROM the pin state, never read back out of
        // the composite selection paths, which the pickers may rewrite.
        const sourceDataIds = {
          ...orchestrator.aiSourceDataIds,
          insightIds: selectedInsightsAnalysisId ? [selectedInsightsAnalysisId] : [],
        };
        const strategyData = buildStrategyData();

        // Create strategy with server-side multi-phase AI pipeline
        const result = await orchestrator.services.aiOrchestration.strategyOrchestrator.createWithAIServer(
          strategyData as any,
          sourceDataIds,
          {
            generateScreens,
            model: aiModel,
            maxTokens: aiMaxTokens,
            // Phase F: pin the user-selected InsightsAnalysis so generation grounds on IT.
            // Without this the backend silently falls back to "latest completed".
            // The Select pin is first-class state (race-immune vs selection paths).
            insightsAnalysisId: selectedInsightsAnalysisId || undefined,
            insightVariableGenerationId: selectedInsightVariableGenerationId || undefined,
            variableIds: selectedVariableIds.length > 0 ? selectedVariableIds : undefined,
            // AC-019: the per-variable insight selection. Empty = every accepted
            // variable of the generation (the resolver's CLI back-compat path).
            insightVariableIds: selectedInsightVariableIds.length > 0
              ? selectedInsightVariableIds
              : undefined,
            // W13 (IS2): the wizard's «Insights» step owns the create-mode
            // selection. `insightIds` is omitted while every DISPLAYED thesis
            // is checked (the server then renders today's projection,
            // AC-W13-31); `scopes` names what the step displayed so a scope it
            // never showed is never judged (W13-R22); `includeSources` always
            // travels — the switch decided it either way (AC-W13-24).
            ...(createInsightSelection ? { insightSelection: createInsightSelection } : {}),
            useProjectVariables: true,
            // UXR C7: the all-on default is OMITTED so unrestricted prompts stay
            // byte-identical (AC-047); a real restriction travels as-is.
            availableBlocks: isDefaultAvailableBlocks(availableBlocksValue, blockSubtypeCatalog.subtypes)
              ? undefined
              : availableBlocksValue,
            // Block Examples Library (Phase 3): zero selections OMIT the key —
            // baseline payload stays byte-identical (regression guard).
            ...(libraryBlockSelections.length > 0 ? { libraryBlockSelections } : {}),
          },
        );

        /**
         * F-LIVE2-40 + ruling FW9-R3 — TWO failure branches, and NEITHER of them
         * closes the dialog on this tick, because a message in a dialog that
         * closes is a message nobody reads (that is exactly what fix wave 9
         * shipped for the second branch).
         *
         *  - `refused`: the request was validated away and the orchestrator
         *    deleted the document it had just created. Nothing exists, nothing
         *    is handed to `onSave`, and the operator fixes the request here.
         *  - `!success` without `refused`: a 5xx / network / timeout answer whose
         *    server-side effect is unknown, so the document SURVIVES and the
         *    «created, but the generation failed» sentence stays true. The
         *    created strategy still reaches the caller — `pendingCreatedStrategy`
         *    hands it to `onSave` when the operator closes the dialog, and both
         *    launch actions are gated meanwhile (FW9-R5) so a second press
         *    cannot create a second document.
         *
         * Both render the SAME inline `Alert`: the review step carries it in the
         * wizard, the quick-create screen carries it in quick create. The wizard
         * is moved to the step that shows it; quick create is NOT — switching it
         * to an invisible review step is what made the message vanish.
         */
        if (!result.success) {
          setSubmitError({
            message: result.error ?? 'Unknown error',
            refused: Boolean(result.refused),
          });
          if (!result.refused && result.strategy) {
            setPendingCreatedStrategy(result.strategy);
            // The document holds what the form says, so the operator is not
            // asked about unsaved changes on the way out of a failed generation.
            acceptChanges();
          }
          if (!quickCreateSurfaceActive) setActiveStep('review');
          return;
        }
        setSubmitError(null);
        if (result.taskId) {
          console.log('[StrategyFormDialog] ✅ AI generation task started:', result.taskId);
        }

        if (!result.strategy) {
          throw new Error('Strategy creation returned no strategy document');
        }
        savedStrategy = result.strategy;
      } else {
        // Update existing strategy (no AI)
        if (!strategyId) {
          throw new Error('Strategy ID is required for edit mode');
        }

        console.log('[StrategyFormDialog] Updating strategy...');
        console.log('[StrategyFormDialog] Selected source data paths:', formData.selectedSourceDataPaths);

        // ✅ Use buildStrategyData() for consistent save (includes variables, sourceDataMapping, etc.)
        const strategyData = buildStrategyData();
        savedStrategy = await strategyService.update(strategyId, strategyData as Partial<Strategy>);
      }

      // ✅ Accept changes after successful save
      acceptChanges();

      // Call onSave callback with saved strategy
      onSave(savedStrategy);

      // Close dialog
      onClose();

      // Reset state
      setErrors({});
      reset();

      // ✅ Return focus to trigger
      returnFocus();
    } catch (error) {
      console.error('[StrategyFormDialog] Error saving strategy:', error);
      alert(`Failed to save strategy: ${(error as Error)?.message || 'Unknown error'}`);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    validateForm,
    formData,
    mode,
    projectId,
    strategyId,
    strategyService,
    orchestrator.services.aiOrchestration.strategyOrchestrator,
    orchestrator.firestoreData,
    orchestrator.aiSourceDataIds,
    buildStrategyData,
    onSave,
    onClose,
    acceptChanges,
    reset,
    returnFocus,
    cloneProjectThemeToStrategy,
    generateScreens,
    aiModel,
    aiMaxTokens,
    selectedInsightVariableGenerationId,
    selectedVariableIds,
    selectedInsightVariableIds,
    availableBlocksValue,
    blockSubtypeCatalog.subtypes,
    selectedInsightsAnalysisId,
    createInsightSelection,
    starvedInsightScopes,
    insightThesisGateReason,
    libraryBlockSelections,
    libraryAvailabilityConflicts,
    quickCreateSurfaceActive,
    pendingCreatedStrategy,
  ]);

  /**
   * Ruling FW9-R3: the strategy a failed generation left behind reaches the
   * caller when the dialog closes — `onSave` (the list refresh) BEFORE
   * `onClose`, exactly the order a successful create uses. Idempotent: the ref
   * is cleared as it is read, so a close through the guard cannot save twice.
   */
  const flushPendingCreatedStrategy = useCallback(() => {
    if (!pendingCreatedStrategy) return;
    setPendingCreatedStrategy(null);
    onSave(pendingCreatedStrategy);
  }, [onSave, pendingCreatedStrategy]);

  // ✅ Handle close with unsaved changes check (memoized)
  const handleClose = useCallback(() => {
    /**
     * Ruling FW9-R5c: while this dialog session's strategy already EXISTS (a
     * create whose generation failed — FW9-R3), closing never asks about unsaved
     * changes. The guard's whole offer is «Save & Close», and there is nothing
     * here that could save: create mode holds no `strategyId`, so the form is
     * bound to no document, and the strategy the server created is edited from
     * the canvas. Offering it would be a button that saves nothing.
     *
     * So the edits made after the failure ARE dropped, on purpose and without a
     * question the dialog cannot honour. The notice is the footer line the
     * operator has been looking at since the failure — «The strategy was already
     * created — close the dialog to open it.» — and closing does exactly that:
     * the created strategy goes to `onSave` (the caller's list refresh), then
     * `onClose`. Every close entry point routes through here (header X, Escape,
     * backdrop), so there is one rule, not one per affordance.
     */
    // ✅ Check for unsaved changes before closing
    if (!pendingCreatedStrategy && isDirty()) {
      setShowUnsavedDialog(true);
      return;
    }

    // No changes, safe to close
    flushPendingCreatedStrategy();
    onClose();
    setErrors({});
    reset();

    // ✅ Return focus to trigger
    returnFocus();
  }, [isDirty, reset, onClose, returnFocus, flushPendingCreatedStrategy, pendingCreatedStrategy]);

  // ✅ Handlers for UnsavedChangesDialog (memoized)
  const handleSaveAndClose = useCallback(async () => {
    /**
     * Ruling FW9-R5b, kept as DEFENCE IN DEPTH and UNREACHABLE by the UI since
     * FW9-R5c: `handleClose` no longer opens the Unsaved-Changes guard while a
     * created strategy is held, so this dialog cannot be on screen in that state
     * and «Save & Close» can never be the thing that saves nothing. The branch
     * stays because the rule belongs to the function — a future caller that
     * opens the guard some other way must not be able to submit a second create
     * for the same attempt. It closes the way `handleClose` does.
     */
    if (pendingCreatedStrategy) {
      setShowUnsavedDialog(false);
      flushPendingCreatedStrategy();
      onClose();
      setErrors({});
      reset();
      returnFocus();
      return;
    }
    if (validateForm()) {
      await handleSubmit();
    }
    setShowUnsavedDialog(false);
  }, [
    validateForm,
    handleSubmit,
    pendingCreatedStrategy,
    flushPendingCreatedStrategy,
    onClose,
    reset,
    returnFocus,
  ]);

  const handleDiscardAndClose = useCallback(() => {
    // Revert to the state the dialog OPENED with — the pristine baseline, not
    // the one the preselection moved (Task 11). The tracked state carries the
    // analysis pin alongside the form fields.
    revert();
    const pristine = pristineBaselineRef.current;
    if (pristine) {
      const {
        pinnedAnalysisId: originalPin,
        // Tracked-only fields (B9): they describe the block choices, they are
        // not part of `FormData` and must not be written back into it.
        libraryBlockSelectionsSignature: _libraryBaseline,
        availableBlocksSignature: _availableBlocksBaseline,
        ...originalForm
      } = pristine;
      setFormData(originalForm);
      setPinnedAnalysisId(originalPin);
    }
    setErrors({});
    flushPendingCreatedStrategy();
    onClose();
    setShowUnsavedDialog(false);
    reset();

    // ✅ Return focus to trigger
    returnFocus();
  }, [revert, reset, onClose, returnFocus, flushPendingCreatedStrategy]);

  const handleContinueEditing = useCallback(() => {
    setShowUnsavedDialog(false);
  }, []);

  /**
   * What the guard LISTS (Task 11, AC-036). The tracked selection is an ARRAY
   * of paths, and the shared detector diffs arrays element by element: one
   * unchecked variable shifts every later index and would be reported as a wall
   * of `selectedSourceDataPaths[7]` rows that tells the operator nothing. The
   * whole selection plane therefore collapses into ONE row, and the analysis pin
   * is shown by name instead of by id.
   */
  const guardChanges = useMemo<ChangedField[]>(() => {
    const changes = getChanges();
    const isSelection = (change: ChangedField) => change.path.startsWith('selectedSourceDataPaths');
    const named: ChangedField[] = changes
      .filter(change => !isSelection(change))
      .map((change): ChangedField => {
        if (change.path === 'pinnedAnalysisId') {
          return {
            ...change,
            path: 'Insight analysis',
            newValue: change.newValue ? (pinnedAnalysisName ?? change.newValue) : change.newValue,
          };
        }
        // B9 fix (D-2): both block-choice signatures are machine-readable —
        // the guard names them the way the wizard's steps do, in counts.
        if (change.path === 'libraryBlockSelectionsSignature') {
          return {
            ...change,
            path: 'Building blocks',
            oldValue: `${librarySignatureCount(String(change.oldValue ?? ''))} selected`,
            newValue: `${librarySignatureCount(String(change.newValue ?? ''))} selected`,
          };
        }
        if (change.path === 'availableBlocksSignature') {
          return {
            ...change,
            path: 'Block types',
            oldValue: change.oldValue == null ? 'all types' : 'a restricted set',
            newValue: `${availableBlocksValue.blockTypes.length} of ${STRATEGY_BLOCK_TYPE_CATALOG.length} types`
              + (availableBlocksValue.subtypeSlugs.length > 0
                ? ` · ${availableBlocksValue.subtypeSlugs.length} subtypes`
                : ''),
          };
        }
        return change;
      });

    if (!changes.some(isSelection)) return named;

    return [
      ...named,
      {
        path: 'Source data selection',
        oldValue: `${trackedBaselineRef.current?.selectedSourceDataPaths.length ?? 0} selected`,
        newValue: `${formData.selectedSourceDataPaths.length} selected`,
        changeType: 'modified',
      },
    ];
  }, [getChanges, formData.selectedSourceDataPaths, pinnedAnalysisName, availableBlocksValue]);

  // BlockFormDialog handlers (Sprint 5)
  const handleAddBlock = useCallback(() => {
    console.log('[StrategyFormDialog] Opening BlockFormDialog in CREATE mode');
    setBlockMode('create');
    setSelectedBlock(null);
    setBlockDialogOpen(true);
  }, []);

  const handleEditBlock = useCallback((block: StrategyBlock) => {
    console.log('[StrategyFormDialog] Opening BlockFormDialog in EDIT mode:', block);
    setBlockMode('edit');
    setSelectedBlock(block);
    setBlockDialogOpen(true);
  }, []);

  const handleBlockSave = useCallback((savedBlock: StrategyBlock) => {
    console.log('[StrategyFormDialog] Block saved:', savedBlock);
    console.log('[StrategyFormDialog] Block mode was:', blockMode);
    // Real-time listener in BlocksListView will update the list automatically
    setBlockDialogOpen(false);
  }, [blockMode]);

  const handleBlockClose = useCallback(() => {
    console.log('[StrategyFormDialog] Block dialog closed');
    setBlockDialogOpen(false);
  }, []);

  // ✅ Memoized action objects to prevent re-renders.
  // W12 Part B: in CREATE mode the SDialog footer is EMPTY — the wizard renders
  // its own footer inside the body column, so «Create with AI» is contained only
  // on the review step (owner ruling 2026-09-10). Edit mode is untouched.
  const primaryAction = useMemo(
    () => (mode === 'create' ? undefined : {
      key: 'primary',
      label: isSubmitting ? 'Saving...' : 'Save',
      onClick: handleSaveOnly,
      disabled: isSubmitting,
      'aria-label': 'Save strategy changes',
      'data-testid': 'strategy-form-save-btn',
    }),
    [isSubmitting, mode, handleSaveOnly],
  );

  const secondaryAction = undefined;

  /**
   * W13 IS2 (REVIEW tier 2 round 2, MF-2): the Insights step's thesis table
   * fills the dialog's height and scrolls INSIDE its own box, so the summary
   * — the only statement of what is SENT (ruling W13-R27) — stays on screen.
   * The dialog's content box must therefore be bounded to the scroller's
   * height on that step (`SDialog contentFill`); every other step keeps the
   * shipped block content box that scrolls as a whole.
   */
  const wizardFillsContent = mode === 'create'
    && activeStep === 'insights'
    && !(quickCreateActive && !quickEditingFromQuickCreate);

  // ── Create wizard: gating, rail, footer ────────────────────────────────────

  /** Step 1 is the only required decision; everything else works with defaults. */
  const wizardPinned = Boolean(selectedInsightsAnalysisId);

  /**
   * The ONE reason «Create with AI» is unavailable. Same three sources as the
   * pre-wizard helper line, now rendered by the wizard footer under the primary.
   */
  const wizardLaunchDisabledReason = useMemo(() => {
    if (mode !== 'create') return undefined;
    /**
     * Ruling FW9-R5, and it comes FIRST because it makes every other reason
     * irrelevant: this dialog already created its strategy (a non-refused
     * generation failure keeps the document, FW9-R3), so «Create with AI» would
     * create a SECOND one. Not a duplicate of a server gate (FW9-R1) — it states
     * a document that exists on this client's own account. The refused branch
     * never reaches here: nothing was created, so the operator may retry.
     */
    if (pendingCreatedStrategy) return LAUNCH_ALREADY_CREATED_REASON;
    const parts = [
      !formData.name.trim() ? 'To enable «Create with AI», add a strategy name.' : null,
      readinessHelperText,
      gatingVariablePackageReason,
      libraryConflictReason,
      // W13 IS2 fix round 1 (I1 + I3): the run may not start while the theses
      // on screen belong to another analysis, nor silently fall back to the
      // default projection because they could not be read at all.
      insightThesisGateReason,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : undefined;
  }, [
    mode,
    formData.name,
    readinessHelperText,
    gatingVariablePackageReason,
    libraryConflictReason,
    insightThesisGateReason,
    pendingCreatedStrategy,
  ]);

  /**
   * Waiting is not failing (Task B9d fix 6's rule, applied to I1): while the
   * thesis plane is the ONLY thing gating the launch, the line under the primary
   * is neutral — a red sentence for a read in flight would claim a defect.
   */
  const wizardLaunchDisabledTone: 'neutral' | 'error' | undefined =
    wizardLaunchDisabledReason === INSIGHT_THESES_LOADING_REASON
      // FW9-R5: the failure is already stated in red by the inline Alert above;
      // a second red sentence under the primary would claim a second defect.
      || wizardLaunchDisabledReason === LAUNCH_ALREADY_CREATED_REASON
      ? 'neutral'
      : undefined;

  /**
   * The gates the launch primary is actually subject to, in ONE place: the
   * review step's chip, the quick-create chip and the primary itself all read
   * this (Task B7's one-source rule, extended by m46 to the SPEC-019 library
   * conflict). Quick create adds `hydrating` on top; nothing else differs.
   */
  const launchGateInput = useMemo(() => ({
    result: feasibility,
    nameMissing: !formData.name.trim(),
    variablePackageBlocked: Boolean(gatingVariablePackageReason),
    libraryConflict: libraryAvailabilityConflicts.length > 0,
    submitting: isSubmitting,
    // FW9-R5: the review chip and the quick-create chip report the gate too —
    // neither may read «Ready to generate» over a disabled primary (m46).
    alreadyCreated: Boolean(pendingCreatedStrategy),
  }), [
    feasibility,
    formData.name,
    gatingVariablePackageReason,
    libraryAvailabilityConflicts,
    isSubmitting,
    pendingCreatedStrategy,
  ]);

  /** The review step's verdict — the same value that disables its primary. */
  const wizardLaunchStatus = useMemo(
    () => launchReadinessStatus(launchGateInput),
    [launchGateInput],
  );

  /**
   * Rail items — live one-line summaries of what each step will send. The
   * caption/status RULES live in `buildWizardRailItems` (pure, unit-tested);
   * the dialog only feeds it the counts the submit handler would serialise.
   */
  const railItems = useMemo<WizardRailItem[]>(() => buildWizardRailItems({
    pinnedAnalysisName,
    pinned: Boolean(pinnedAnalysisId),
    generationChosen: Boolean(selectedInsightVariableGenerationId),
    selectedTaskCount: submittedTaskIds.length,
    availableTaskCount: wizardSourceRows.tasks.rows.length,
    selectedInsightVariableCount: selectedInsightVariableIds.length,
    availableInsightVariableCount: wizardSourceRows.insightVariables.rows.length,
    // The EFFECTIVE package (an empty picker selection means «send them all»),
    // never the raw path list — the caption must not read «0 · follow tasks»
    // while every variable is on its way.
    selectedProjectVariableCount,
    availableProjectVariableCount: wizardSourceRows.projectVariables.rows.length,
    variablesFollowTasks,
    selectedSocialProofCount: selectedSocialProofIds.length,
    taskRoutesBlocked: feasibility.blockers.some((blocker) => blocker.code === 'no_task_routes'),
    // W13 IS2: what the request is grounded in, and whether the selection
    // starved a displayed scope (the step that owns it is the step the rail
    // paints red — AC-W13-04).
    selectedInsightThesisCount: checkedInsightThesisIds.length,
    availableInsightThesisCount: visibleInsightThesisRows.length,
    insightSelectionNarrows,
    insightRowsFailed,
    insightSelectionStarved: starvedInsightScopes.length > 0,
    strategyTypeCaption: strategyTypeLabel(formData.strategyType),
    allowedBlockTypeCount: availableBlocksValue.blockTypes.length,
    selectedSubtypeCount: availableBlocksValue.subtypeSlugs.length,
    libraryExampleCount: libraryBlockSelections.length,
    // SPEC-019 (m46): the step that owns the contradiction is the step the rail
    // paints red — the operator must not have to hunt for it.
    libraryConflict: libraryAvailabilityConflicts.length > 0,
    modelShortName: modelPricing.shortName,
    generateScreens,
    variablePackageBlocked: feasibility.blockers.some(
      (blocker) => blocker.code === 'empty_variable_package',
    ),
    // Task B9d must-fix 3: «Review & create» never reads ✓ while the readiness
    // panel two lines below it says the run is blocked, and the step the
    // operator is standing on is always the blue numeral.
    readinessBlocked: feasibility.state === 'blocked',
    activeStep,
    visitedSteps,
  }), [
    activeStep,
    feasibility.state,
    pinnedAnalysisId,
    pinnedAnalysisName,
    submittedTaskIds,
    checkedInsightThesisIds,
    visibleInsightThesisRows,
    insightSelectionNarrows,
    insightRowsFailed,
    starvedInsightScopes,
    selectedInsightVariableGenerationId,
    selectedInsightVariableIds,
    selectedProjectVariableCount,
    selectedSocialProofIds,
    variablesFollowTasks,
    wizardSourceRows.tasks.rows.length,
    wizardSourceRows.insightVariables.rows.length,
    wizardSourceRows.projectVariables.rows.length,
    feasibility.blockers,
    formData.strategyType,
    availableBlocksValue,
    libraryBlockSelections,
    libraryAvailabilityConflicts,
    modelPricing.shortName,
    generateScreens,
    visitedSteps,
  ]);

  /**
   * Task B7: the quick-create rows. Values are the RAIL captions of the same
   * steps (one caption source, never a second opinion); only the details — the
   * selected route names, the variable composition, the social-proof mix and
   * the estimate — are added here, from state the dialog already owns.
   */
  const selectedRouteNames = useMemo<string[]>(() => submittedTaskIds.map((routeTaskId: string) => (
    formTaskList.find((task: { id: string }) => task.id === routeTaskId)?.name ?? routeTaskId
  )), [submittedTaskIds, formTaskList]);

  const quickCreateRows = useMemo(() => buildQuickCreateRows({
    rail: railItems,
    routes: submittedTaskIds.length,
    generateScreens,
    estimate: estimateGeneration({
      routes: submittedTaskIds.length,
      generateScreens,
      modelOutputUsdPerMTokens: modelPricing.outputUsdPerMTokens,
    }),
    routeNames: selectedRouteNames,
    projectVariablesSummary: variablesSummaryCaption,
    // I3: the quick screen names the failure as loudly as the wizard does.
    ...(insightRowsFailed ? { insightsDetail: INSIGHT_THESES_UNREADABLE_CAPTION } : {}),
    socialProof: readinessSocialProof,
  }), [
    railItems,
    submittedTaskIds.length,
    generateScreens,
    modelPricing.outputUsdPerMTokens,
    selectedRouteNames,
    variablesSummaryCaption,
    insightRowsFailed,
    readinessSocialProof,
  ]);

  /**
   * The launch actions, shared by the review STEP and the quick-create screen
   * (Task B7): one «Create with AI», one disabled reason, one `describedBy` —
   * quick create is never a second, divergent launch surface.
   */
  const launchFooterActions = useMemo(() => ({
    onSaveWithoutGenerating: () => { void handleSaveOnly(); },
    onCreateWithAI: () => { void handleSubmit(); },
    submitting: isSubmitting,
    disabledReason: wizardLaunchDisabledReason,
    ...(wizardLaunchDisabledTone ? { disabledReasonTone: wizardLaunchDisabledTone } : {}),
    describedBy: readinessDescribedBy,
    // FW9-R5: «Save without generating» would create a SECOND document too, so
    // the already-created gate reaches it — and only that gate does.
    saveWithoutGeneratingBlocked: Boolean(pendingCreatedStrategy),
  }), [
    handleSaveOnly,
    handleSubmit,
    isSubmitting,
    wizardLaunchDisabledReason,
    wizardLaunchDisabledTone,
    readinessDescribedBy,
    pendingCreatedStrategy,
  ]);

  const wizardFooter = useMemo<WizardFooterProps>(() => {
    // A step reached from a quick-create row: the only way on is back to the
    // quick screen, and both labels say so.
    if (quickEditingFromQuickCreate) {
      return {
        kind: 'fromQuick',
        onBackToQuick: handleQuickCreateReturn,
        onDone: handleQuickCreateReturn,
      };
    }

    const index = wizardStepIndex(activeStep);
    const onBack = index > 0
      ? () => handleWizardStepChange(WIZARD_STEPS[index - 1].key)
      : undefined;

    if (activeStep === 'review') {
      return {
        kind: 'review',
        onBack: onBack ?? (() => handleWizardStepChange('analysis')),
        ...launchFooterActions,
      };
    }

    const next = WIZARD_STEPS[index + 1];
    return {
      kind: 'steps',
      onBack,
      onSkipToReview: () => handleWizardStepChange('review'),
      onNext: () => handleWizardStepChange(next.key),
      nextLabel: `Next: ${next.label}`,
      disabledReason: wizardPinned
        ? undefined
        : 'Pin an insight analysis to continue — every other step works with defaults.',
    };
  }, [
    activeStep,
    handleQuickCreateReturn,
    handleWizardStepChange,
    launchFooterActions,
    quickEditingFromQuickCreate,
    wizardPinned,
  ]);

  /**
   * The quick screen's readiness chip. Label AND blocked flag come from ONE
   * function fed the same three gates the primary is fed (`wizardLaunchDisabledReason`
   * = name · feasibility · variable package, plus `isSubmitting`) — so the chip
   * cannot read «Ready to generate» while the primary next to it is disabled.
   */
  /**
   * Quick create is still hydrating: the list has not arrived, or it has arrived
   * with analyses in it and the auto-pin has not produced a pin yet.
   */
  const quickCreateHydrating = quickCreateActive
    && !pinnedAnalysisId
    && (analysisHydration.loading || !analysisHydration.empty);

  const quickCreateReadiness = useMemo(() => launchReadinessStatus({
    ...launchGateInput,
    hydrating: quickCreateHydrating,
  }), [launchGateInput, quickCreateHydrating]);

  /**
   * The quick screen's footer IS the review footer — minus a Back it has nowhere
   * to go. While the screen hydrates the ONE reason is the hydration one: the
   * name and blocker gates below it are counted against data that has not
   * arrived (Task B9d fix 6).
   */
  const quickCreateFooter = useMemo<WizardFooterProps>(
    () => ({
      kind: 'review',
      ...launchFooterActions,
      ...(quickCreateReadiness.reason
        ? {
          // The verdict's own sentence: hydration («Loading insight analyses…»)
          // or, per FW9-R5, «already created» — the SAME sentence the wizard's
          // footer shows, because both read one verdict. Neutral in both cases:
          // nothing is failing at this instant (the failure, when there was one,
          // is stated in red by the inline Alert above).
          disabledReason: quickCreateReadiness.reason,
          disabledReasonTone: 'neutral' as const,
          describedBy: undefined,
        }
        : {}),
    }),
    [launchFooterActions, quickCreateReadiness.reason],
  );

  /**
   * Generate dialog title based on mode and strategy name.
   * @param mode - Dialog mode ('create' or 'edit')
   * @param strategyName - Strategy name for edit mode
   * @returns Dialog title string
   */
  const getDialogTitle = (mode: 'create' | 'edit', strategyName?: string): string => {
    if (mode === 'create') {
      return 'Create Strategy';
    }
    return `Edit ${strategyName || 'Strategy'}`;
  };

  // ✅ FR-010: Dialog title "Create Strategy with AI" in create mode
  // ✅ FR-011: Dynamic title in edit mode "Edit {strategy name}"
  const dialogTitle = getDialogTitle(mode, initialData?.name);

  /**
   * Strategy Type / Description / Tone / Design — EDIT mode only since Task
   * B4. Create mode renders the thin `DetailsToneStep` (case 'details'
   * below), which owns the same fields as its own props-in/JSX-out body.
   */
  const detailsFields = (
    <>
      <StyledSelectDropdown
        label="Strategy Type"
        value={formData.strategyType || ''}
        onChange={(value) =>
          handleFieldChange('strategyType', value as Strategy['strategyType'])
        }
        options={STRATEGY_TYPES.map((type) => ({ value: type, label: STRATEGY_TYPE_LABELS[type] }))}
        error={Boolean(errors.strategyType)}
        errorMessage={errors.strategyType}
        required
        fullWidth
        data-testid="strategy-form-type"
      />
      {/* ✅ FR-015: Description auto-expand to ~200px with scroll */}
      <StyledTextArea
        label="Description"
        value={formData.description}
        onChange={(value) => handleFieldChange('description', value)}
        error={Boolean(errors.description)}
        errorMessage={errors.description}
        fullWidth
        rows={4}
        maxRows={8}
        maxLength={500}
        showCharacterCount
        data-testid="strategy-form-description"
      />
      <StyledTextField
        label="Tone of Voice"
        value={formData.toneOfVoice}
        onChange={(value) => handleFieldChange('toneOfVoice', value)}
        fullWidth
        placeholder="e.g., Professional, Friendly, Casual"
        data-testid="strategy-form-tone-of-voice"
      />
      <StyledTextArea
        label="Design Description"
        value={formData.designDescription}
        onChange={(value) => handleFieldChange('designDescription', value)}
        fullWidth
        rows={3}
        placeholder="Describe the visual design style..."
        data-testid="strategy-form-design-description"
      />
    </>
  );

  /**
   * The EDIT-mode selection surface. Since Task B3 the create wizard owns its
   * own selection (four `SelectionTable` steps writing whole-entity paths), so
   * this composite — and every create-only branch it used to carry — belongs to
   * edit mode alone: today's flat form is untouched by W12 Part B.
   */
  const sourceDataBody = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Select audiences, tasks, and study cases to provide context for AI-generated content.
        The AI will use this data to create relevant strategy blocks and screens. You can modify
        the source data selection for this strategy.
      </Typography>

      {/* Task 11 (AC-036): the operator touching the selection freezes the
          unsaved-changes baseline. Captured on pointer/key DOWN — before the
          click changes anything — so the very first toggle is already a listed
          change while the machine's preselection waves before it are not. */}
      <Box
        onPointerDownCapture={freezeSelectionBaseline}
        onKeyDownCapture={freezeSelectionBaseline}
      >
        <CompositeDataPicker
          {...orchestrator.compositeProps}
          layout="vertical"
          crossFilterByInsight
          pickers={[
            { type: 'insights' as const, label: 'Insight Analysis (required)', enabled: true, selectAll: false },
            { type: 'task' as const, label: 'Tasks', enabled: true, selectAll: true },
            { type: 'variable' as const, label: 'Variables', enabled: true, selectAll: false },
            { type: 'socialproof' as const, label: 'Social Proof', enabled: true, selectAll: false },
          ]}
          variables={Object.keys(strategyVars).length > 0 ? strategyVars : (orchestrator.services.aiOrchestration.project as any)?.variableSet?.variables ?? {}}
          taskList={formTaskList}
        />
      </Box>

      <Typography variant="caption" color="text.secondary">
        Note: Changing source data will not regenerate AI content automatically. Use the
        regenerate function to update blocks with new data.
      </Typography>
    </Box>
  );


  const exportStrategyJsonButton = (
    <Box sx={{ display: 'flex', justifyContent: 'flex-start', pt: 1 }}>
      <Button
        size="small"
        variant="text"
        startIcon={<FileDownloadIcon />}
        onClick={handleExportStrategyJson}
        sx={{ textTransform: 'none', color: 'text.secondary' }}
      >
        Export Strategy JSON
      </Button>
    </Box>
  );

  /**
   * Step 1's body, built ONCE: the wizard renders it as `case 'analysis'` and
   * quick create renders the very same element at the top of its screen
   * (Task B7). `autoPinLatest` is the only difference between the two — quick
   * create opens on the latest completed analysis, the wizard opens unpinned.
   */
  const analysisStepProps: AnalysisStepProps = {
    projectId,
    // Quick create shows the pin it OWNS. `selectedInsightsAnalysisId` also
    // accepts an analysis derived from a selection PATH, which carries no task
    // ids — a quick screen opened on one would have no routes to show and no
    // defaults to apply. Quick create therefore re-pins the latest analysis
    // properly instead of inheriting a half-state (the wizard keeps showing the
    // path-derived value, exactly as before).
    analysisId: (quickCreateActive ? pinnedAnalysisId : selectedInsightsAnalysisId) || null,
    onAnalysisSelect: handleAnalysisSelect,
    acceptedVariableCounts: acceptedVariableCountsByAnalysis,
    name: formData.name,
    onNameChange: handleNameChange,
    nameError: errors.name,
    generationOptions: analysisStepGenerationOptions,
    generationId: selectedInsightVariableGenerationId,
    onGenerationChange: handleGenerationChange,
    autoPinLatest: quickCreateActive,
    onHydrationChange: handleAnalysisHydrationChange,
    disabled: isSubmitting,
  };

  /**
   * Step map. `analysis` is the Task B2 body (`AnalysisStep`), the four
   * selection steps are the Task B3 `SelectionTable` surfaces, and `details`
   * is the Task B4 `DetailsToneStep` body — all of the approved canvas v3.3;
   * the rest are still the TEMPORARY Task B1 flattening of today's create-mode
   * sections — no `SAccordion` anywhere. Tasks B5–B6 replace those.
   */
  const renderCreateStep = (): React.ReactNode => {
    switch (activeStep) {
      case 'analysis':
        return <AnalysisStep {...analysisStepProps} />;

      case 'tasks':
        return (
          <TasksStep
            rows={wizardSourceRows.tasks.rows}
            selectedIds={submittedTaskIds}
            onSelectedIdsChange={handleTaskSelectionChange}
            loading={wizardSourceRows.tasks.loading}
            error={wizardSourceRows.tasks.error}
            pinned={Boolean(pinnedAnalysisId)}
          />
        );

      case 'insights':
        return (
          <InsightsStep
            rows={visibleInsightThesisRows}
            selectedIds={checkedInsightThesisIds}
            onSelectedIdsChange={handleInsightThesisSelectionChange}
            pinned={Boolean(pinnedAnalysisId)}
            loading={wizardSourceRows.insight.loading}
            error={wizardSourceRows.insight.error}
            starvedScopes={starvedInsightScopes}
            onSelectScopeTheses={handleSelectScopeTheses}
            budgetWarnings={insightBudgetWarnings}
            disabled={isSubmitting}
          />
        );

      case 'insightVars':
        return (
          <InsightVariablesStep
            rows={wizardSourceRows.insightVariables.rows}
            selectedIds={selectedInsightVariableIds}
            onSelectedIdsChange={handleInsightVariableSelectionChange}
            // The GENERATION list and the generation's VARIABLES are two loads;
            // the step reports both, so neither failure is swallowed (FP-7).
            loading={wizardSourceRows.insightVariables.loading || insightVariableGenerationsLoading}
            error={wizardSourceRows.insightVariables.error ?? insightVariableGenerationError}
            summary={insightVariablesSummaryCaption}
            // The ESCAPE from a blocking variable package. The three create-mode
            // Alerts are gone (B6's readiness rows own blocker PRESENTATION),
            // but the way OUT must survive: the blockers that name a bad
            // generation leave the table non-empty, so the empty-state action
            // would never render.
            blockingReason={gatingVariablePackageReason}
            generationChosen={Boolean(selectedInsightVariableGenerationId)}
            onReviewVariables={onReviewVariables && insightVariablesReviewTarget
              ? () => onReviewVariables(insightVariablesReviewTarget)
              : undefined}
          />
        );

      case 'projectVars':
        return (
          <ProjectVariablesStep
            rows={visibleProjectVariableRows}
            selectedIds={selectedVariableIds}
            onSelectedIdsChange={handleProjectVariableSelectionChange}
            followTasks={variablesFollowTasks}
            onFollowTasksChange={setVariablesFollowTasks}
            summary={variablesSummaryCaption}
            productCountLabel={`Product variables: ${selectedProjectVariableCount}/${projectVariableCount}`}
            // AC-020: a SELECTED/ACCEPTED ratio, exactly like the product one.
            insightCountLabel={`Insight-derived: ${selectedInsightVariableIds.length}/${acceptedInsightVariableCount}`}
            hiddenByFollowCount={hiddenProjectVariableCount}
            disabled={isSubmitting}
          />
        );

      case 'proof':
        return (
          <SocialProofStep
            rows={socialProofRows}
            selectedIds={selectedSocialProofIds}
            onSelectedIdsChange={handleSocialProofSelectionChange}
            loading={wizardSourceRows.socialProof.loading}
            error={wizardSourceRows.socialProof.error}
          />
        );

      case 'details':
        return (
          <DetailsToneStep
            strategyType={formData.strategyType}
            onStrategyTypeChange={(value) => handleFieldChange('strategyType', value)}
            description={formData.description}
            onDescriptionChange={(value) => handleFieldChange('description', value)}
            toneOfVoice={formData.toneOfVoice}
            onToneChange={(value) => handleFieldChange('toneOfVoice', value)}
            designDescription={formData.designDescription}
            onDesignDescriptionChange={(value) => handleFieldChange('designDescription', value)}
            disabled={isSubmitting}
          />
        );

      case 'blockTypes':
        return (
          <BlockTypesStep
            subtypes={blockSubtypeCatalog.subtypes}
            value={availableBlocksValue}
            onChange={(next) => {
              setAvailableBlocksTouched(true);
              setAvailableBlocksValue(next);
            }}
            disabled={isSubmitting}
          />
        );

      case 'library':
        return (
          <LibraryStep
            projectExamples={blockLibraryExamples.examples}
            systemExamples={systemBlockLibrary.examples}
            strategyVariables={strategyVars}
            value={libraryBlockSelections}
            onChange={setLibraryBlockSelections}
            // SPEC-019 (AC-P3-039): a materialized library selection whose
            // blockType «Block types» excludes is a HARD conflict — named on
            // the step that can fix it and refused on submit (server re-enforces).
            conflicts={libraryAvailabilityConflicts}
            strategyName={formData.name}
            disabled={isSubmitting}
            loading={blockLibraryExamples.loading || systemBlockLibrary.loading}
            error={blockLibraryExamples.error?.message ?? systemBlockLibrary.error?.message ?? null}
          />
        );

      case 'generation':
        return (
          <AiSettingsStep
            projectId={projectId}
            model={aiModel}
            onModelChange={setAiModel}
            pricing={modelPricing}
            blockGenerationMode={blockGenerationMode}
            onBlockGenerationModeChange={setBlockGenerationMode}
            maxTokens={aiMaxTokens}
            // The step owns the bounds rule (`sanitizeMaxOutputTokens` per
            // keystroke, `clampMaxOutputTokens` on commit) — the dialog only
            // stores what the field already validated.
            onMaxTokensChange={setAiMaxTokens}
            // W13 IS2 (ruling R1): the quotes switch lives with the other
            // «how the run is made» decisions; the THESES are chosen on their
            // own step.
            includeInsightSources={includeInsightSources}
            onIncludeInsightSourcesChange={setIncludeInsightSources}
            disabled={isSubmitting}
            modelLoading={projectDefaultModel.loading}
          />
        );

      case 'review':
      default:
        return (
          <ReviewStep
            readiness={(
              <GenerationReadinessStrip
                layout="rows"
                result={feasibility}
                // m46: the chip next to «Create with AI» reports the LAUNCH
                // verdict, so it can never read «Ready to generate» while the
                // primary below it is disabled.
                launchStatus={wizardLaunchStatus}
                analysisName={readinessAnalysisName}
                routeNames={readinessRouteNames}
                socialProof={readinessSocialProof}
                insightVariablesAccepted={selectedInsightVariableGeneration
                  ? acceptedInsightVariableCount
                  : undefined}
                // MF-5: the thesis blocker's contained fix, the same one the
                // step's own Alert carries (designer invariant 26).
                onSelectAllTheses={handleSelectScopeTheses}
                insightScopeNames={insightScopeNames}
                // NOT `insightTheses`: on the review step the «Also sent»
                // «Insights» row states the theses AND the quotes switch, and a
                // number is stated ONCE per screen (experience principle 1).
                taskScopedVariablesWhenSelected={returningTaskScopedVariableCount}
                onSelectAllRoutes={handleSelectAllRoutes}
                onAdjust={handleAdjustSourceData}
                onEdit={handleReviewEdit}
              />
            )}
            variables={reviewVariablesSummary}
            generateScreens={generateScreens}
            onGenerateScreensChange={setGenerateScreens}
            routes={submittedTaskIds.length}
            modelName={modelPricing.shortName || aiModel}
            modelInputUsdPerMTokens={modelPricing.inputUsdPerMTokens}
            modelOutputUsdPerMTokens={modelPricing.outputUsdPerMTokens}
            alsoSent={reviewAlsoSentRows}
            onEdit={handleReviewEdit}
            footer={exportStrategyJsonButton}
            adaptNeedsScreens={adaptNeedsScreens}
            submitError={submitError}
          />
        );
    }
  };

  return (
    <>
      {/* ✅ Main Form Dialog - Full screen mode for blocks management */}
      <SDialog
        primaryAction={primaryAction}
        secondaryAction={secondaryAction}
        actions={mode === 'edit' && strategyId ? [{
          label: 'Generate blocks with AI…',
          onClick: () => setGenerateForSavedOpen(true),
          variant: 'outlined' as const,
          disabled: isSubmitting || generateForSavedSubmitting || Boolean(generateForSavedDisabledReason),
          startIcon: <AutoAwesomeIcon />,
          'data-testid': 'strategy-form-generate-btn',
          'aria-label': 'Generate blocks with AI for this saved strategy',
        }] : undefined}
        open={open}
        onClose={handleClose}
        fullScreen={true}
        fullWidth
        title={dialogTitle}
        contentFill={wizardFillsContent}
        // mode="modal"
        // ✅ ARIA attributes for accessibility. SPEC-020: aria-labelledby
        // pointed at a NON-EXISTENT id ("strategy-form-title") — the paper-slot
        // aria-label is the working channel (BaseDialog a11y trap).
        // Create mode has no intro paragraph any more (the step subtitle does that
        // job), so the reference would dangle — a dangling aria-describedby is a
        // worse a11y state than none.
        aria-describedby={mode === 'create' ? undefined : 'strategy-form-description'}
        slotProps={{ paper: { 'aria-label': dialogTitle } }}
        data-testid="strategy-form-dialog"
      >
        {/* <DialogHeader
          title={dialogTitle}
          onClose={handleClose}
        /> */}

        {mode === 'create' ? (
          quickCreateSurfaceActive ? (
            /* Task B7: the one-screen repeat path. Same 800px column and same
               footer slot as the wizard shell, so switching modes does not move
               the page — and the footer IS the review footer. */
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', minWidth: 0 }}>
              <QuickCreateScreen
                analysis={analysisStepProps}
                rows={quickCreateRows}
                onChange={handleQuickCreateChange}
                ready={quickCreateReadiness}
                onSwitchToGuided={handleSwitchCreateMode}
                // F-LIVE2-40 (review #2): quick create is a launch surface, so
                // it carries the submit's failure — the same Alert, same copy.
                submitError={submitError}
              />
              <WizardFooter {...quickCreateFooter} />
            </Box>
          ) : (
          <StrategyCreateWizard
            activeStep={activeStep}
            onStepChange={handleWizardStepChange}
            rail={railItems}
            stepBody={renderCreateStep()}
            pinned={wizardPinned}
            firstRun={existingStrategyCount === 0 && !firstRunDismissed}
            onDismissFirstRun={() => setFirstRunDismissed(true)}
            footer={wizardFooter}
            guideService={guideService}
            // The way INTO quick create — on the ENTRY step only (approved
            // canvas v3.3: `Main.html` carries the link, `Step2…Step10` do not).
            // Hidden while a step is open from quick create as well: the
            // `fromQuick` footer already owns the way back, and two competing
            // exits would read as two different destinations.
            headerSwitch={activeStep === 'analysis' && !quickEditingFromQuickCreate
              ? { label: 'Quick create instead', onClick: handleSwitchCreateMode }
              : undefined}
          />
          )
        ) : (
        <Stack spacing={3}>
          {/* ✅ Clear description for context */}
          <Typography
            id="strategy-form-description"
            variant="body2"
            color="text.secondary"
          >
            Update the strategy information below. Required fields are marked with *.
          </Typography>

          {/* ✅ FR-012: Flat layout without SmartSection groupings (except Source Data Selection) */}
          {/* ✅ FR-013: No helper text under fields */}
          <StyledTextField
            label="Name"
            value={formData.name}
            onChange={(value) => handleFieldChange('name', value)}
            error={Boolean(errors.name)}
            errorMessage={errors.name}
            required
            fullWidth
            maxLength={100}
            data-testid="strategy-form-name"
          />

          {/* Generation readiness (Task 10) — directly under the header in edit
              mode (AC-037). It is a read-only re-presentation of the SUBMITTED
              selection: opening it changes nothing (designer invariant 3). */}
          <GenerationReadinessStrip
            result={feasibility}
            analysisName={readinessAnalysisName}
            routeNames={readinessRouteNames}
            socialProof={readinessSocialProof}
            insightVariablesAccepted={selectedInsightVariableGeneration
              ? acceptedInsightVariableCount
              : undefined}
            insightTheses={readinessInsightTheses}
            taskScopedVariablesWhenSelected={returningTaskScopedVariableCount}
            onSelectAllRoutes={handleSelectAllRoutes}
            onAdjust={handleAdjustSourceData}
          />

          {detailsFields}

          {/* ✅ Sprint 11: Source Data Selection for AI (edit mode) */}
          {/* ✅ FR-014: HelpTooltip in section header */}
          <SAccordion
            title="Source Data Selection (AI)"
            collapsible
            expanded={sourceDataExpanded}
            onChange={setSourceDataExpanded}
            data-testid="strategy-form-section-source-data"
            helpContent="Select audiences, tasks, and study cases to provide context for AI-generated strategy content. The AI will analyze this data to create relevant blocks and screens."
          >
            {sourceDataBody}
          </SAccordion>

          {/* ✅ Strategy Variables (edit mode) */}
          <SAccordion
            title="Strategy Variables"
            collapsible
            data-testid="strategy-form-section-variables"
            helpContent="Variables cloned from project at creation. Edit independently for experimentation. Promote successful changes back to project."
          >
            {strategyId ? (
              /* Edit mode: editable with promote + clone from project buttons */
              <>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <Button
                  size="small"
                  variant="text"
                  onClick={async () => {
                    const confirmed = window.confirm(
                      'Clone variables from project?\n\nThis will replace all strategy variables with the current project variables.'
                    );
                    if (!confirmed) return;
                    try {
                      const { VariableEditingService } = await import('@modules/firebase-core/services/VariableEditingService');
                      const projectVars = await new VariableEditingService().copyProjectToStrategy(projectId, strategyId, strategyVars);
                      setStrategyVars(projectVars);
                    } catch (err) {
                      alert('Failed to clone variables: ' + (err as Error).message);
                    }
                  }}
                  sx={{ textTransform: 'none', color: 'text.secondary' }}
                >
                  Clone from Project
                </Button>
              </Box>
              <VariableSetEditor
                variables={strategyVars}
                tasks={formTaskList}
                onChange={async (newVars, reviewed) => {
                  const { VariableEditingService } = await import('@modules/firebase-core/services/VariableEditingService');
                  await new VariableEditingService().saveStrategy(projectId, strategyId, newVars, reviewed);
                  setStrategyVars(newVars);
                }}
                onPromoteToProject={async () => {
                  const confirmed = window.confirm(
                    'Promote strategy variables to project?\n\nThis creates a new project variable version from this strategy\'s variables.'
                  );
                  if (!confirmed) return;
                  try {
                    const varsToPromote = strategyVars;
                    await variableVersionService.promoteFromStrategy(
                      projectId,
                      strategyId!,
                      varsToPromote,
                      undefined,
                      `Promoted from strategy "${initialData?.name}"`,
                    );
                    alert('Variables promoted to project successfully!');
                  } catch (err) {
                    console.error('Failed to promote variables:', err);
                    alert('Failed to promote variables: ' + (err as Error).message);
                  }
                }}
                disabled={isSubmitting}
              />
              </>
            ) : null}
          </SAccordion>

          {/* UXR C4 (AC-015/AC-016): generation status + why the footer entry is disabled. */}
          {strategyId && (
            <Typography
              variant="caption"
              color="text.secondary"
              data-testid="strategy-form-last-generation-status"
            >
              {lastGenerationTask
                ? `Last generation: ${lastGenerationTask.status}${lastGenerationTask.createdAt
                  ? ` · ${lastGenerationTask.createdAt.toLocaleString()}`
                  : ''}`
                : 'No generations for this strategy yet.'}
              {generateForSavedDisabledReason ? ` ${generateForSavedDisabledReason}` : ''}
            </Typography>
          )}

          {/* ✅ Strategy Theme Section (edit mode only) */}
          {strategyId && (
            <SAccordion
              title="Strategy Theme"
              collapsible
              defaultCollapsed={true}
              helpContent="Visual theme for strategy screens. Cloned from project theme on creation. Edit to customize colors, fonts, and screen templates independently."
            >
              <Box
                data-testid="strategy-theme-preview-section"
                sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
              >
                {strategyThemeForEditor ? (
                  <Box
                    data-testid="strategy-theme-preview-card"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 2,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {/* Color swatch */}
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1,
                        bgcolor: strategyThemeForEditor.background?.kind === 'solid'
                          ? (strategyThemeForEditor.background.content as { color: string })?.color ?? '#1a1a1a'
                          : '#1a1a1a',
                        border: `2px solid ${strategyThemeForEditor.colors?.buttonsBackground ?? '#ffc201'}`,
                        flexShrink: 0,
                      }}
                    />
                    <Box flex={1}>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        data-testid="strategy-theme-preview-name"
                      >
                        {strategyThemeData?.sourceThemeId ? 'Cloned from project theme' : 'Custom theme'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {Object.keys(strategyThemeForEditor.flowDocument?.screens ?? {}).length} screen templates
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<PaletteIcon />}
                      onClick={() => setThemeEditorOpen(true)}
                    >
                      Edit Theme
                    </Button>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', py: 1 }}>
                    No theme attached to this strategy. Save the strategy first, then a theme will be cloned from the project.
                  </Typography>
                )}
              </Box>
            </SAccordion>
          )}

          {/* ✅ FR-029: Blocks List Section (Sprint 4) - edit mode only */}
          {strategyId && (
            <Box sx={{ mt: 3 }}>
              <BlocksListView
                projectId={projectId}
                strategyId={strategyId}
                strategyBlockService={strategyBlockService!}
                onAddBlock={handleAddBlock}
                onEdit={handleEditBlock}
                onRowClick={handleEditBlock}
              />
            </Box>
          )}
          {exportStrategyJsonButton}
        </Stack>
        )}

        {/* ✅ FR-019: Only primary action button (no Cancel button) */}
        {/* <DialogFooter primaryAction={primaryAction} /> */}
      </SDialog>

      {/* UXR C4: destructive confirm for regenerating the SAVED strategy. */}
      {generateForSavedOpen && (
        <SDialog
          open={true}
          onClose={() => setGenerateForSavedOpen(false)}
          maxWidth="sm"
          fullWidth
          title="Generate blocks with AI"
          showCloseButton={false}
          data-testid="strategy-form-generate-confirm-dialog"
          actions={[{
            label: 'Cancel',
            onClick: () => setGenerateForSavedOpen(false),
            variant: 'outlined' as const,
            disabled: generateForSavedSubmitting,
            'data-testid': 'strategy-form-generate-confirm-cancel',
          }]}
          primaryAction={{
            label: generateForSavedSubmitting ? 'Starting…' : 'Generate',
            onClick: () => void handleConfirmGenerateForSaved(),
            color: 'warning' as const,
            disabled: generateForSavedSubmitting,
            'data-testid': 'strategy-form-generate-confirm-submit',
          }}
        >
          <Stack spacing={1.5}>
            <Typography variant="body1">
              Regenerate «{initialData?.name || 'this strategy'}» from its saved inputs?
            </Typography>
            <Typography variant="body2" color="warning.main">
              Existing blocks and their screens will be replaced by the new generation.
              Variables, theme, and version history are kept. The task appears in the
              tasks panel.
            </Typography>
            <FormControlLabel
              control={(
                <Switch
                  size="small"
                  checked={generateForSavedScreens}
                  onChange={(_, checked) => setGenerateForSavedScreens(checked)}
                  data-testid="strategy-form-generate-confirm-screens"
                />
              )}
              label="Also generate screens for each block"
              slotProps={{ typography: { variant: 'body2', color: 'text.secondary' } }}
            />
            {/* SG-A (T7b): READ-ONLY readback of the mode this run will use.
                Editing it after creation is out of scope (SG2); a legacy
                strategy without the field reads «Parallel». */}
            <BlockGenerationModeReadback
              strategy={initialData as unknown as Record<string, unknown> | undefined}
            />
          </Stack>
        </SDialog>
      )}

      {/* ✅ Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        open={showUnsavedDialog}
        changedFields={guardChanges}
        onSave={handleSaveAndClose}
        onDiscard={handleDiscardAndClose}
        onCancel={handleContinueEditing}
        saving={isSubmitting}
        title="Unsaved Changes Detected"
        message="You have made changes to the strategy form. What would you like to do with these changes?"
        saveButtonText="Save & Close"
        discardButtonText="Discard Changes"
        cancelButtonText="Continue Editing"
      />

      {/* BlockFormDialog (Sprint 5) - Edit mode only */}
      {mode === 'edit' && strategyId && (
        <BlockFormDialog
          open={blockDialogOpen}
          mode={blockMode}
          projectId={projectId}
          strategyId={strategyId}
          blockId={selectedBlock?.id}
          initialData={blockMode === 'edit' ? selectedBlock ?? undefined : undefined}
          onClose={handleBlockClose}
          onSave={handleBlockSave}
          triggerRef={addBlockButtonRef}
        />
      )}

      {/* Strategy Theme Editor Dialog */}
      <ThemeV2EditorDialog
        open={themeEditorOpen}
        theme={strategyThemeForEditor}
        projectId={projectId}
        title="Edit Strategy Theme"
        onClose={() => setThemeEditorOpen(false)}
        onSave={handleSaveStrategyTheme}
      />
    </>
  );
};
